import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Stack, DatePicker, IconButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Dropdown, Pivot, PivotItem, DetailsList, DetailsListLayoutMode, SelectionMode, ProgressIndicator, Dialog as FluentDialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, TextField } from '@fluentui/react';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { DetailsRow } from '@fluentui/react/lib/DetailsList';
import { Button, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent, Input, Label, makeStyles, Drawer, DrawerHeader, DrawerHeaderTitle, DrawerBody } from '@fluentui/react-components';
import { Dismiss24Regular, AddSquare28Regular, ChevronRight20Regular, ChevronDown20Regular } from '@fluentui/react-icons';
import { FiArrowLeft, FiFilter, FiDownload, FiEdit2, FiRotateCcw, FiGrid, FiFolder, FiPhone, FiMoreVertical, FiTrash2, FiMove, FiSave } from 'react-icons/fi';
import { CiFilter } from 'react-icons/ci';
import { useTranslation } from 'react-i18next';

import { DismissFilled, Add20Filled, EditRegular, Edit24Regular, ArrowMove24Regular, ArrowDownload24Regular, Delete24Regular } from "@fluentui/react-icons";
import Widget from '../Widgets/Widget';
import { inboundCallsDashboard } from '../../Config/dashboardConfigs';
import { exportToPDF } from '../../Utils/pdfExport';
import { WidgetTypes } from '../../Types/widgetTypes';
import DashboardFilters from './DashboardFilters';
import WidgetCatalog, { WIDGET_CATALOG } from './WidgetCatalog';
import TabManager from './TabManager';
import WidgetInstanceConfig from './WidgetInstanceConfig';
import AnalyticsWidgetCatalogPanel from './AnalyticsWidgetCatalogPanel';
import AnalyticsWidgetConfigPanel from './AnalyticsWidgetConfigPanel';
import { ConfirmAlert } from '../GenericComponent/ConfirmAlert';
import { StaticMetricWidget, StaticBarChartWidget, StaticDonutChartWidget, StaticLineChartWidget, StaticGaugeWidget, STATIC_WIDGET_DATA } from '../Widgets/StaticWidgets';
import { FluentMetricWidget, FluentBarChartWidget, FluentDonutChartWidget, FluentLineChartWidget, FluentHorizontalBarChartWidget } from '../Widgets/FluentUIWidgets';
import { fetchWidgetData, saveDashboardLayout, getDashboardLayout } from '../../Services/AnalyticsService';
import { startExcelExport, pollExportStatus } from '../../Services/ExportService';
import { updateScope, getScopeById, GetAnalyticsProfile } from '../../Services/HeedifyServices';
import { setScopes } from '../../Redux/Actions/ScopesActions';
import { setAnalyticsProfiles } from '../../Redux/Actions/AnalyticsActions';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './AnalyticsDashboard.css';

import { registerIcons } from '@fluentui/react/lib/Styling';

// Register missing icons with simple SVGs
registerIcons({
    icons: {
        clear: (
            <svg viewBox="0 0 2048 2048" width="1em" height="1em">
                <path d="M1115 1024l690 691-90 90-691-690-691 690-90-90 690-691-690-691 90-90 691 690 691-690 90 90-690 691z" fill="currentColor" />
            </svg>
        ),
        info: (
            <svg viewBox="0 0 2048 2048" width="1em" height="1em">
                <path d="M1024 0q141 0 272 36t245 103 207 160 160 207 103 245 36 272q0 141-36 272t-103 245-160 207-207 160-245 103-272 36q-141 0-272-36t-245-103-207-160-160-207-103-245-36-272q0-141 36-272t103-245 160-207 207-160T752 36t272-36zm128 1408v-640H896v640h256zM896 640h256V384H896v256z" fill="currentColor" />
            </svg>
        )
    }
});

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Calculate date range based on custom period
 * @param {string} period - Period type (today, yesterday, week, month, quarter, year)
 * @returns {object} - Object with startDate and endDate as ISO strings
 */
const calculateDateRangeFromPeriod = (period) => {
    const now = new Date();
    const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
    let startDate, endDate;

    switch (period) {
        case 'today':
            startDate = new Date(today);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1); // Fin de la journée
            break;
        
        case 'yesterday':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 1);
            endDate = new Date(today);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'week':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 7);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'month':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 30);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'quarter':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 90);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        case 'year':
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 365);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
            break;
        
        default:
            // Default to last 30 days
            startDate = new Date(today);
            startDate.setUTCDate(startDate.getUTCDate() - 30);
            endDate = new Date(today);
            endDate.setUTCDate(endDate.getUTCDate() + 1);
            endDate.setUTCMilliseconds(-1);
    }

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
    };
};

// Styles pour le dialog d'ajout de tab
const useDialogStyles = makeStyles({
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
});

// Widgets displayed in the hardcoded "Dashboard" tab (= Overview content)
const DASHBOARD_WIDGET_IDS = [
    'widget-total-calls',
    'widget-answered-calls',
    'widget-abandoned-calls',
    'widget-outbound-calls',
    'widget-service-level',
    'widget-average-wait-time',
    'widget-avg-inbound-call-duration',
    'widget-avg-outbound-call-duration',
    'widget-sla-trend',
    'widget-call-status-distribution',
    'widget-call-volume-daily'
];

// Default widget positions for the Dashboard tab (lg: 12 cols) — Overview layout
const DEFAULT_WIDGET_POSITIONS = {
    // Row 1 — KPIs
    "widget-total-calls":               { x: 0, y: 0, w: 3, h: 2 },
    "widget-answered-calls":            { x: 3, y: 0, w: 3, h: 2 },
    "widget-abandoned-calls":           { x: 6, y: 0, w: 3, h: 2 },
    "widget-outbound-calls":            { x: 9, y: 0, w: 3, h: 2 },

    // Row 2 — KPIs
    "widget-service-level":             { x: 0, y: 2, w: 3, h: 2 },
    "widget-average-wait-time":         { x: 3, y: 2, w: 3, h: 2 },
    "widget-avg-inbound-call-duration": { x: 6, y: 2, w: 3, h: 2 },
    "widget-avg-outbound-call-duration":{ x: 9, y: 2, w: 3, h: 2 },

    // Row 3 — SLA trend + status donut
    "widget-sla-trend":                 { x: 0, y: 4, w: 6, h: 4 },
    "widget-call-status-distribution":  { x: 6, y: 4, w: 6, h: 4 },

    // Row 4 — Daily volume
    "widget-call-volume-daily":         { x: 0, y: 8, w: 12, h: 4 }
};

// Responsive positions for medium screens (md: 10 cols)
// Stack metrics vertically (2 per row x 5 cols), charts full width
const MD_WIDGET_POSITIONS = {
    // === ROW 1: Main Call Metrics (2 widgets x 5 cols) ===
    "widget-total-calls": { x: 0, y: 0, w: 5, h: 2 },
    "widget-answered-calls": { x: 5, y: 0, w: 5, h: 2 },

    // === ROW 2: More Call Metrics (2 widgets x 5 cols) ===
    "widget-abandoned-calls": { x: 0, y: 2, w: 5, h: 2 },
    "widget-outbound-calls": { x: 5, y: 2, w: 5, h: 2 },

    // === ROW 3: Performance Metrics (2 widgets x 5 cols) ===
    "widget-average-wait-time": { x: 0, y: 4, w: 5, h: 2 },
    "widget-avg-inbound-call-duration": { x: 5, y: 4, w: 5, h: 2 },

    // === ROW 4: More Performance Metrics (2 widgets x 5 cols) ===
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 5, h: 2 },
    "widget-service-level": { x: 5, y: 6, w: 5, h: 2 },

    // === ROW 5: Additional Metrics (1 widget x 5 cols) ===
    "widget-transferred-calls": { x: 0, y: 8, w: 5, h: 2 },

    // === ROW 6: Charts (2 widgets x 5 cols = 10 cols) ===
    "widget-call-volume-hourly": { x: 0, y: 10, w: 5, h: 4 },
    "widget-call-status-distribution": { x: 5, y: 10, w: 5, h: 4 },

    // === ROW 7: Distribution Charts (2 widgets x 5 cols) ===
    "widget-calls-by-queue": { x: 0, y: 14, w: 5, h: 4 },
    "widget-calls-by-flow": { x: 5, y: 14, w: 5, h: 4 },

    // === ROW 8: Agent Chart (1 widget full width) ===
    "widget-calls-per-agent": { x: 0, y: 18, w: 10, h: 4 },

    // === ROW 9+: Full width charts ===
    "widget-call-volume-daily": { x: 0, y: 22, w: 10, h: 4 },
    "widget-peak-hours": { x: 0, y: 26, w: 10, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 31, w: 10, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 37, w: 10, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 44, w: 10, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 49, w: 10, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 54, w: 10, h: 7 },
    "widget-sla-trend": { x: 0, y: 61, w: 10, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 66, w: 10, h: 10 }
};

// Responsive positions for small screens (sm: 6 cols)
// 2 widgets per row (3 cols each) for metrics, full width for charts
const SM_WIDGET_POSITIONS = {
    // Metrics (2 per row)
    "widget-total-calls": { x: 0, y: 0, w: 3, h: 2 },
    "widget-answered-calls": { x: 3, y: 0, w: 3, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 2, w: 3, h: 2 },
    "widget-outbound-calls": { x: 3, y: 2, w: 3, h: 2 },
    "widget-average-wait-time": { x: 0, y: 4, w: 3, h: 2 },
    "widget-avg-inbound-call-duration": { x: 3, y: 4, w: 3, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 3, h: 2 },
    "widget-service-level": { x: 3, y: 6, w: 3, h: 2 },
    "widget-transferred-calls": { x: 0, y: 8, w: 3, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 10, w: 6, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 14, w: 6, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 18, w: 6, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 22, w: 6, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 26, w: 6, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 30, w: 6, h: 4 },
    "widget-peak-hours": { x: 0, y: 34, w: 6, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 39, w: 6, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 45, w: 6, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 52, w: 6, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 57, w: 6, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 62, w: 6, h: 7 },
    "widget-sla-trend": { x: 0, y: 69, w: 6, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 74, w: 6, h: 10 }
};

// Responsive positions for extra small screens (xs: 4 cols)
// 2 widgets per row (2 cols each) for metrics, full width for charts
const XS_WIDGET_POSITIONS = {
    // Metrics (2 per row)
    "widget-total-calls": { x: 0, y: 0, w: 2, h: 2 },
    "widget-answered-calls": { x: 2, y: 0, w: 2, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 2, w: 2, h: 2 },
    "widget-outbound-calls": { x: 2, y: 2, w: 2, h: 2 },
    "widget-average-wait-time": { x: 0, y: 4, w: 2, h: 2 },
    "widget-avg-inbound-call-duration": { x: 2, y: 4, w: 2, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 6, w: 2, h: 2 },
    "widget-service-level": { x: 2, y: 6, w: 2, h: 2 },
    "widget-transferred-calls": { x: 0, y: 8, w: 2, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 10, w: 4, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 14, w: 4, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 18, w: 4, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 22, w: 4, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 26, w: 4, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 30, w: 4, h: 4 },
    "widget-peak-hours": { x: 0, y: 34, w: 4, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 39, w: 4, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 45, w: 4, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 52, w: 4, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 57, w: 4, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 62, w: 4, h: 7 },
    "widget-sla-trend": { x: 0, y: 69, w: 4, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 74, w: 4, h: 10 }
};

// Responsive positions for mobile (xxs: 2 cols)
// Full width vertical stack
const XXS_WIDGET_POSITIONS = {
    // Metrics (stacked vertically, full width)
    "widget-total-calls": { x: 0, y: 0, w: 2, h: 2 },
    "widget-answered-calls": { x: 0, y: 2, w: 2, h: 2 },
    "widget-abandoned-calls": { x: 0, y: 4, w: 2, h: 2 },
    "widget-outbound-calls": { x: 0, y: 6, w: 2, h: 2 },
    "widget-average-wait-time": { x: 0, y: 8, w: 2, h: 2 },
    "widget-avg-inbound-call-duration": { x: 0, y: 10, w: 2, h: 2 },
    "widget-avg-outbound-call-duration": { x: 0, y: 12, w: 2, h: 2 },
    "widget-service-level": { x: 0, y: 14, w: 2, h: 2 },
    "widget-transferred-calls": { x: 0, y: 16, w: 2, h: 2 },

    // Charts (full width)
    "widget-call-volume-hourly": { x: 0, y: 18, w: 2, h: 4 },
    "widget-call-status-distribution": { x: 0, y: 22, w: 2, h: 4 },
    "widget-calls-by-queue": { x: 0, y: 26, w: 2, h: 4 },
    "widget-calls-by-flow": { x: 0, y: 30, w: 2, h: 4 },
    "widget-calls-per-agent": { x: 0, y: 34, w: 2, h: 4 },
    "widget-call-volume-daily": { x: 0, y: 38, w: 2, h: 4 },
    "widget-peak-hours": { x: 0, y: 42, w: 2, h: 5 },
    "widget-queue-kpi-summary": { x: 0, y: 47, w: 2, h: 6 },
    "widget-month-to-date-summary": { x: 0, y: 53, w: 2, h: 7 },
    "widget-calls-served-time-ranges": { x: 0, y: 60, w: 2, h: 5 },
    "widget-calls-abandoned-time-ranges": { x: 0, y: 65, w: 2, h: 5 },
    "widget-daily-sla-stats": { x: 0, y: 70, w: 2, h: 7 },
    "widget-sla-trend": { x: 0, y: 77, w: 2, h: 5 },
    "widget-daily-abandon-stats": { x: 0, y: 82, w: 2, h: 10 }
};


/**
 * Analytics dashboard for calls
 * Uses inboundCallsDashboard configuration to display widgets
 */
const AnalyticsDashboard = ({ tenantId, token }) => {
    const { t } = useTranslation();
    const dialogStyles = useDialogStyles();

    // Get scope from URL
    const { scope } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const decodedScope = decodeURIComponent(scope || 'default');

    // Récupérer les scopes depuis Redux
    const scopesData = useSelector(state => state.reducer.SCOPES);
    const analyticsProfiles = useSelector(state => state.reducer.ANALYTICS_PROFILES);

    // État pour les filtres de date
    const [filterValues, setFilterValues] = useState({
        startDate: null,
        endDate: null
    });

    // État pour le timezone sélectionné (défaut: timezone du navigateur)
    const [selectedTimezone, setSelectedTimezone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    // Liste des timezones communes (surtout US pour superviseurs)
    const COMMON_TIMEZONES = [
        { key: 'America/New_York', text: '🇺🇸 US Eastern (New York)' },
        { key: 'America/Chicago', text: '🇺🇸 US Central (Chicago)' },
        { key: 'America/Denver', text: '🇺🇸 US Mountain (Denver)' },
        { key: 'America/Los_Angeles', text: '🇺🇸 US Pacific (Los Angeles)' },
        { key: 'America/Phoenix', text: '🇺🇸 US Arizona (Phoenix)' },
        { key: 'America/Anchorage', text: '🇺🇸 US Alaska (Anchorage)' },
        { key: 'Pacific/Honolulu', text: '🇺🇸 US Hawaii (Honolulu)' },
        { key: 'Europe/London', text: '🇬🇧 UK (London)' },
        { key: 'Europe/Paris', text: '🇫🇷 France (Paris)' },
        { key: 'Europe/Berlin', text: '🇩🇪 Germany (Berlin)' },
        { key: 'Europe/Madrid', text: '🇪🇸 Spain (Madrid)' },
        { key: 'Asia/Tokyo', text: '🇯🇵 Japan (Tokyo)' },
        { key: 'Asia/Shanghai', text: '🇨🇳 China (Shanghai)' },
        { key: 'Asia/Singapore', text: '🇸🇬 Singapore' },
        { key: 'Australia/Sydney', text: '🇦🇺 Australia (Sydney)' },
        { key: 'UTC', text: '🌍 UTC (Universal)' },
        { key: Intl.DateTimeFormat().resolvedOptions().timeZone, text: `📍 ${t('BROWSER_TIMEZONE')}` }
    ];

    // État pour les filtres appliqués (déclenchent le rafraîchissement des widgets)
    const [appliedFilters, setAppliedFilters] = useState(null);

    // État pour les filtres dynamiques (flows, queues, agents)
    const [dynamicFilters, setDynamicFilters] = useState({});

    // État pour l'export PDF
    const [isExporting, setIsExporting] = useState(false);

    // État pour le layout du dashboard
    const [layouts, setLayouts] = useState(null);

    // État pour afficher/masquer les filtres
    const [showFilters, setShowFilters] = useState(false);

    // État pour le mode édition (déplacement des widgets)
    const [isEditMode, setIsEditMode] = useState(false);

    // États pour éviter les réinitialisations pendant drag/resize
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    // État pour les données pré-chargées des widgets
    const [widgetsData, setWidgetsData] = useState({});
    const [isLoadingWidgets, setIsLoadingWidgets] = useState(false);

    // État pour l'onglet actif principal (Dashboard ou Calls List)
    const [selectedTab, setSelectedTab] = useState('dashboard');

    // État pour la liste des appels
    const [callsList, setCallsList] = useState([]);
    const [isLoadingCalls, setIsLoadingCalls] = useState(false);
    
    // États pour les filtres de colonnes du Calls List
    const [callsFilters, setCallsFilters] = useState({});
    const [callsFilterVisible, setCallsFilterVisible] = useState({});
    const tableContainerRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedTranscription, setSelectedTranscription] = useState(null);
    const [showTranscriptionPanel, setShowTranscriptionPanel] = useState(false);
    const [recordingUrls, setRecordingUrls] = useState({}); // Store recording URLs by callId
    const [transcriptions, setTranscriptions] = useState({}); // Store transcriptions by callId

    // État pour l'export Excel
    const [isExportingExcel, setIsExportingExcel] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [exportMessage, setExportMessage] = useState('');
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportDownloadUrl, setExportDownloadUrl] = useState(null);

    // Ref pour suivre l'état précédent du mode édition
    const wasEditModeRef = useRef(false);

    // Ref pour tracker si on vient de charger depuis Cosmos (ne pas réinitialiser pendant édition)
    const isLoadingFromCosmosRef = useRef(false);
    
    // Ref pour tracker si on vient de sauvegarder (éviter rechargement complet)
    const isSavingWidgetRef = useRef(false);
    
    // Ref pour toujours avoir les valeurs à jour de widgetInstances (éviter closure stale)
    const widgetInstancesRef = useRef({});

    // Dans Redux, SCOPES contient { scopeName: scopeUUID }
    const scopeUUID = scopesData?.[decodedScope];

    // État pour stocker le scope complet chargé depuis l'API
    const [currentScopeData, setCurrentScopeData] = useState(null);

    // Tabs personnalisés (en plus de Dashboard et Calls List)
    const [customTabs, setCustomTabs] = useState([]);

    // Instances de widgets pour les tabs personnalisés uniquement
    const [widgetInstances, setWidgetInstances] = useState({});
    const [widgetInstancesLoaded, setWidgetInstancesLoaded] = useState(false);  // Flag pour éviter race condition
    
    // Synchroniser la ref avec le state
    useEffect(() => {
        widgetInstancesRef.current = widgetInstances;
    }, [widgetInstances]);

    // Positions des widgets par tab: {tabId: {instanceId: {x, y, w, h}}}
    const [tabWidgetsPositions, setTabWidgetsPositions] = useState({});

    // Nouveau: Layout state pour chaque tab avec react-grid-layout
    const [tabLayouts, setTabLayouts] = useState({});

    // Dialog states
    const [isCatalogOpen, setIsCatalogOpen] = useState(false);
    const [isTabManagerOpen, setIsTabManagerOpen] = useState(false);
    const [isInstanceConfigOpen, setIsInstanceConfigOpen] = useState(false);
    const [selectedWidgetInfo, setSelectedWidgetInfo] = useState(null);
    const [editingInstance, setEditingInstance] = useState(null);
    const [isAddTabDialogOpen, setIsAddTabDialogOpen] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const [deleteTabConfirm, setDeleteTabConfirm] = useState({ open: false, tabId: null, tabName: '' });

    // New states for the redesigned catalog/config panels
    const [showCatalogPanel, setShowCatalogPanel] = useState(false);
    const [catalogInitiallyCollapsed, setCatalogInitiallyCollapsed] = useState(true);
    const [showConfigPanel, setShowConfigPanel] = useState(false);
    const [selectedWidget, setSelectedWidget] = useState(null);
    const [draggedWidget, setDraggedWidget] = useState(null);
    const [dropZoneActive, setDropZoneActive] = useState(false);

    // Récupérer le dashboard actif - NE PAS filtrer les widgets ici
    const dashboardConfig = React.useMemo(() => {
        const config = inboundCallsDashboard;
        
        // ✅ Retourner TOUS les widgets sans filtrage
        // Le filtrage sera fait au niveau du rendu si nécessaire
        return config;
    }, []);

    /**
     * Old Portal Settings loading removed - now using Analytics Profile service
     * Dashboard layout positions loaded via getDashboardLayout below
     */

    // Charger le scope complet depuis l'API
    // L'API attend le nom du scope ("default"), pas l'UUID
    useEffect(() => {
        const loadScopeData = async () => {
            if (!tenantId || !token || !decodedScope) return;

            try {
                const scopeResponse = await getScopeById(tenantId, decodedScope, token);

                // 🔹 Extract the actual scope object (handle nested structure)
                const scopeData = scopeResponse?.scope || scopeResponse;

                setCurrentScopeData(scopeData);

                // 🔹 Chercher le profile analytics par le champ 'scope' au lieu de AnalyticProfileId
                if (analyticsProfiles) {
                    // Chercher le profile qui a scope === decodedScope
                    let matchingProfile = Object.values(analyticsProfiles).find(
                        profile => profile.scope === decodedScope
                    );

                    // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
                    if (!matchingProfile) {
                        matchingProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === "default"
                        );
                    }

                    if (matchingProfile) {
                        // Profile found
                    } else {
                        // Profile non trouvé dans Redux - le charger depuis le backend
                     
                        try {
                            const profileResponse = await GetAnalyticsProfile(tenantId, 'ALL', token);

                            if (profileResponse && profileResponse.success && profileResponse.data) {
                                // profileResponse.data peut être un objet ou un tableau
                                const profiles = Array.isArray(profileResponse.data)
                                    ? profileResponse.data
                                    : Object.values(profileResponse.data);

                                // Chercher le profile avec le bon scope
                                const targetProfile = profiles.find(p => p.scope === decodedScope);

                                if (targetProfile) {
                                  
                                    // 🔥 Ajouter le profile à Redux
                                    const updatedProfiles = {
                                        ...analyticsProfiles,
                                        [targetProfile.id]: targetProfile
                                    };
                                    dispatch(setAnalyticsProfiles(updatedProfiles));

                                } else {
                                    console.warn(`⚠️ No analytics profile found for scope "${decodedScope}" in backend response`);
                                }
                            } else {
                                console.error(`❌ Failed to load analytics profiles from backend`);
                            }
                        } catch (loadError) {
                            console.error(`❌ Error loading analytics profiles from backend:`, loadError);
                        }
                    }
                }
            } catch (error) {
                console.error('❌ Error loading scope data:', error);
            }
        };

        loadScopeData();
    }, [tenantId, token, decodedScope, dispatch]);

    // Charger le Dashboard Layout depuis l'Analytics Profile
    useEffect(() => {
        const loadDashboardLayout = async () => {
            if (!tenantId || !token || !decodedScope || !analyticsProfiles) return;

            // 🔹 Trouver le profile analytics par le champ 'scope'
            let analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === decodedScope
            );

            // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
            if (!analyticsProfile) {
                analyticsProfile = Object.values(analyticsProfiles).find(
                    profile => profile.scope === "default"
                );
            }

            if (!analyticsProfile) {
                return;
            }

            try {
                const response = await getDashboardLayout(
                    tenantId,
                    analyticsProfile.id,
                    token
                );

                // 🔎 DEBUG — full raw response from backend
                console.log('🔎 [DEBUG] getDashboardLayout raw response:', JSON.stringify(response, null, 2));

                if (response.success && response.data && response.data.tabs) {
                    const tabs = response.data.tabs;

                    // 🔎 DEBUG — list tabs and their type
                    console.log('🔎 [DEBUG] Tabs in response:', Object.keys(tabs));
                    Object.keys(tabs).forEach(tabId => {
                        console.log(`   → "${tabId}": type=${tabs[tabId].type}, displayName=${tabs[tabId].displayName}, widgets=${Object.keys(tabs[tabId].widgets || {}).length}, positions=${Object.keys(tabs[tabId].widgetsPositions || {}).length}`);
                    });

                    // Vérifier si le dashboard a des positions sauvegardées
                    const dashboardTab = tabs.dashboard;
                    const hasSavedPositions = dashboardTab && dashboardTab.widgetsPositions &&
                        Object.keys(dashboardTab.widgetsPositions).length > 0;

                    // Vérifier si des tabs custom existent (pour ne pas reset si l'utilisateur a déjà personnalisé)
                    const hasCustomTabs = Object.keys(tabs).some(tabId => tabs[tabId].type === 'custom');

                    console.log('🔎 [DEBUG] hasSavedPositions (dashboard tab):', hasSavedPositions, '| hasCustomTabs:', hasCustomTabs);

                    if (!hasSavedPositions && !hasCustomTabs) {
                        // Aucune position sauvegardée ET aucun tab custom: initialiser avec DEFAULT_WIDGET_POSITIONS
                        console.log('🎬 No saved positions AND no custom tabs — initializing with defaults');
                        await initializeDefaultLayout();
                        return; // L'initialisation va recharger les données
                    }

                    if (!hasSavedPositions && hasCustomTabs) {
                        console.log('✅ Dashboard tab empty but custom tabs exist — skipping default init');
                    }

                    console.log('💾 Using saved positions from Cosmos (not defaults)');

                    // Marquer qu'on charge depuis Cosmos pour permettre la mise à jour du layout
                    isLoadingFromCosmosRef.current = true;

                    // Charger les tabs personnalisés
                    const tabsArray = Object.keys(tabs)
                        .filter(tabId => tabs[tabId].type === 'custom')
                        .map(tabId => ({
                            id: tabId,
                            name: tabs[tabId].displayName,
                            type: tabs[tabId].type,
                            createdAt: tabs[tabId].createdAt
                        }));
                    console.log('🔎 [DEBUG] Custom tabs after filter(type==="custom"):', tabsArray);
                    setCustomTabs(tabsArray);

                    // Charger les positions des widgets par tab
                    const positions = {};
                    Object.keys(tabs).forEach(tabId => {
                        if (tabs[tabId].widgetsPositions) {
                            positions[tabId] = tabs[tabId].widgetsPositions;
                        }
                    });
                    setTabWidgetsPositions(positions);
                    setTabLayouts(positions); // Synchroniser avec tabLayouts
                    
                    // ✅ Charger TOUS les widgets avec configs (custom tabs ET widgets dashboard configurés)
                    const allWidgets = {};
                    Object.keys(tabs).forEach(tabId => {
                        const tabWidgets = tabs[tabId].widgets || {};
                        Object.keys(tabWidgets).forEach(widgetInstanceId => {
                            const raw = tabWidgets[widgetInstanceId];
                            // Normalize stored widget instance: some entries may be a string (widgetId)
                            const normalized = (typeof raw === 'string') ? { widgetId: raw } : (raw || {});
                            allWidgets[widgetInstanceId] = {
                                ...normalized,
                                id: widgetInstanceId,
                                tabId: tabId
                            };
                        });
                    });
                    setWidgetInstances(allWidgets);
                    setWidgetInstancesLoaded(true);  // ← Marquer comme chargé

                } else {
                    console.warn('⚠️ [DEBUG] Response missing success/data/tabs:', { success: response?.success, hasData: !!response?.data, hasTabs: !!response?.data?.tabs });
                    // Aucune structure existante: initialiser avec les valeurs par défaut
                    await initializeDefaultLayout();
                }
            } catch (error) {
                console.error('❌ Error loading dashboard layout:', error);
            }
        };

        loadDashboardLayout();
    }, [tenantId, token, decodedScope, analyticsProfiles]);

    // Initialiser les valeurs par défaut des filtres
    useEffect(() => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 7);
        defaultStartDate.setHours(0, 0, 0, 0); // Normaliser à minuit

        const defaultEndDate = new Date();
        defaultEndDate.setHours(0, 0, 0, 0); // Normaliser à minuit

        setFilterValues({
            startDate: defaultStartDate,
            endDate: defaultEndDate
        });

        // Appliquer automatiquement les filtres par défaut
        const filters = {
            startDate: defaultStartDate.toISOString(),
            endDate: defaultEndDate.toISOString(),
            scope: decodedScope  // IMPORTANT: Utiliser decodedScope pour partition pruning
        };

        setAppliedFilters(filters);
    }, [decodedScope]);

    // Construire le layout à partir de la configuration des widgets
    // IMPORTANT: Initialiser avec useMemo pour éviter les recalculs inutiles
    const initialLayouts = React.useMemo(() => {
        if (!dashboardConfig || !dashboardConfig.widgets) {
            return null;
        }

        // Helper function to generate layout for a breakpoint
        const generateLayoutForBreakpoint = (positionsMap) => {
            return dashboardConfig.widgets.map(widget => {
                const position = positionsMap[widget.id];
                return {
                    i: widget.id,
                    x: position?.x ?? widget.layout.x ?? 0,
                    y: position?.y ?? widget.layout.y ?? 0,
                    w: position?.w ?? widget.layout.w ?? 2,
                    h: position?.h ?? widget.layout.h ?? 2,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            });
        };

        // Generate lg layout avec positions par défaut
        const lgLayout = dashboardConfig.widgets.map(widget => {
            const defaultPosition = DEFAULT_WIDGET_POSITIONS[widget.id];

            if (defaultPosition) {
                return {
                    i: widget.id,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    w: defaultPosition.w,
                    h: defaultPosition.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            return {
                i: widget.id,
                x: widget.layout.x,
                y: widget.layout.y,
                w: widget.layout.w,
                h: widget.layout.h,
                minW: widget.layout.minW || 2,
                minH: widget.layout.minH || 2,
                static: false
            };
        });

        return {
            lg: lgLayout,
            md: generateLayoutForBreakpoint(MD_WIDGET_POSITIONS),
            sm: generateLayoutForBreakpoint(SM_WIDGET_POSITIONS),
            xs: generateLayoutForBreakpoint(XS_WIDGET_POSITIONS),
            xxs: generateLayoutForBreakpoint(XXS_WIDGET_POSITIONS)
        };
    }, [dashboardConfig]);

    // Mettre à jour le layout quand les positions sauvegardées changent
    useEffect(() => {
        // ⚠️ NE PAS réinitialiser le layout pendant drag/resize en mode édition
        if (isEditMode && (isDragging || isResizing)) {
            return;
        }

        // ⚠️ NE PAS réinitialiser si on est en mode édition et que ce n'est pas un chargement Cosmos
        if (isEditMode && !isLoadingFromCosmosRef.current) {
            return;
        }

        if (!dashboardConfig || !dashboardConfig.widgets) {
            return;
        }

        // Vérifier si on a des positions sauvegardées depuis Cosmos pour le dashboard
        const savedDashboardPositions = tabWidgetsPositions['dashboard'];
        const hasSavedPositions = savedDashboardPositions && Object.keys(savedDashboardPositions).length > 0;
        
        // Helper function to generate layout for a breakpoint
        const generateLayoutForBreakpoint = (positionsMap) => {
            return dashboardConfig.widgets.map(widget => {
                // ✅ Si on a des positions sauvegardées depuis Cosmos, les utiliser en priorité
                if (hasSavedPositions && savedDashboardPositions[widget.id]) {
                    const savedPos = savedDashboardPositions[widget.id];
                    return {
                        i: widget.id,
                        x: savedPos.x,
                        y: savedPos.y,
                        w: savedPos.w,
                        h: savedPos.h,
                        minW: widget.layout.minW || 2,
                        minH: widget.layout.minH || 2,
                        static: false
                    };
                }

                // ✅ Sinon, utiliser les positions par défaut du breakpoint
                const position = positionsMap[widget.id];
                return {
                    i: widget.id,
                    x: position?.x ?? 0,
                    y: position?.y ?? 0,
                    w: position?.w ?? 2,
                    h: position?.h ?? 2,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            });
        };

        // Generate lg layout: utiliser Cosmos si disponible, sinon DEFAULT_WIDGET_POSITIONS
        const lgLayout = dashboardConfig.widgets.map(widget => {
            // ✅ Priorité 1: Positions sauvegardées dans Cosmos
            if (hasSavedPositions && savedDashboardPositions[widget.id]) {
                const savedPos = savedDashboardPositions[widget.id];
                return {
                    i: widget.id,
                    x: savedPos.x,
                    y: savedPos.y,
                    w: savedPos.w,
                    h: savedPos.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            // ✅ Priorité 2: Positions par défaut (fallback)
            const defaultPosition = DEFAULT_WIDGET_POSITIONS[widget.id];
            if (defaultPosition) {
                return {
                    i: widget.id,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    w: defaultPosition.w,
                    h: defaultPosition.h,
                    minW: widget.layout.minW || 2,
                    minH: widget.layout.minH || 2,
                    static: false
                };
            }

            // ✅ Priorité 3: Config du widget (fallback ultime)
            return {
                i: widget.id,
                x: widget.layout.x,
                y: widget.layout.y,
                w: widget.layout.w,
                h: widget.layout.h,
                minW: widget.layout.minW || 2,
                minH: widget.layout.minH || 2,
                static: false
            };
        });

        const gridLayouts = {
            lg: lgLayout,
            md: lgLayout,  // Utiliser les mêmes positions que lg
            sm: lgLayout,  // Utiliser les mêmes positions que lg
            xs: lgLayout,  // Utiliser les mêmes positions que lg
            xxs: lgLayout  // Utiliser les mêmes positions que lg
        };

        setLayouts(gridLayouts);

        // Réinitialiser la ref après application du layout
        isLoadingFromCosmosRef.current = false;
    }, [dashboardConfig, initialLayouts, tabWidgetsPositions, isEditMode, isDragging, isResizing]);

    // Détecter la sortie du mode édition et sauvegarder les positions
    useEffect(() => {
        // Si on passe de true à false (sortie du mode édition)
        if (wasEditModeRef.current === true && isEditMode === false) {
            // Positions will be saved via "Save Layout" button
        }

        // Mettre à jour la référence
        wasEditModeRef.current = isEditMode;
    }, [isEditMode, layouts]);

    /**
     * Charger les données de TOUS les widgets en parallèle (Promise.all)
     * Optimise le temps de chargement vs requêtes séquentielles
     */
    const loadAllWidgetsData = useCallback(async () => {
        if (!dashboardConfig || !dashboardConfig.widgets) {
            return;
        }

        setIsLoadingWidgets(true);
        const totalWidgets = dashboardConfig.widgets.length;

        try {
            const startTime = performance.now();

            // Fonction pour charger un widget
            const loadWidget = async (widget) => {
                // 🔹 Déterminer les filtres à utiliser pour ce widget
                // ✅ IMPORTANT: Utiliser widgetInstancesRef.current pour avoir TOUJOURS les valeurs à jour
                const instanceId = `dashboard-${widget.id}`;
                const widgetInstance = widgetInstancesRef.current[instanceId];
                
              
                
                let widgetFilters = appliedFilters;  // Par défaut, filtres globaux
                
                // ⚠️ IMPORTANT: Vérifier STRICTEMENT si useGlobalFilters est explicitement false
                if (widgetInstance && widgetInstance.config?.useGlobalFilters === false) {
                    // Mode: FILTRES PERSONNALISÉS du widget (tous optionnels)
                    const customPeriod = widgetInstance.config?.customPeriod;
                    const customFilters = widgetInstance.config?.customFilters || {};
                    
                    widgetFilters = {
                        scope: decodedScope,
                        timezone: selectedTimezone
                    };
                    
                    // Ajouter les dates SEULEMENT si une période est définie
                    if (customPeriod) {
                        const dateRange = calculateDateRangeFromPeriod(customPeriod);
                        widgetFilters.startDate = dateRange.startDate;
                        widgetFilters.endDate = dateRange.endDate;
                    }
                    
                    // Ajouter les filtres flow/queue/agent s'ils sont définis
                    if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                    if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                    if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                    
                    } else {
                    // Mode: FILTRES GLOBAUX (défaut pour tous les widgets non configurés)
                    console.log(`🌍 Dashboard widget ${widget.id} uses GLOBAL filters`);
                }
                
                // Vérifier si le widget utilise reportConfig comme source
                if (widget.config?.dataSource === 'reportConfig' && widget.config?.dataPath) {
                    try {
                        // 🔹 Trouver le profile analytics par le champ 'scope'
                        let analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === decodedScope
                        );

                        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
                        if (!analyticsProfile) {
                            analyticsProfile = Object.values(analyticsProfiles).find(
                                profile => profile.scope === "default"
                            );
                        }

                        if (analyticsProfile && analyticsProfile.reportConfig) {
                            // Extraire la valeur depuis le reportConfig en utilisant le dataPath
                            const pathParts = widget.config.dataPath.split('.');
                            let value = analyticsProfile.reportConfig;
                            
                            // Parcourir le path pour extraire la valeur
                            for (const part of pathParts) {
                                value = value?.[part];
                            }
                            
                            // Vérifier le type de widget pour formater les données correctement
                            if (widget.type === WidgetTypes.DONUT) {
                                // Pour DonutChart: convertir l'objet en segments
                                const colors = widget.config.colors || {};
                                const labels = widget.config.labels || {};
                                
                                const segments = Object.entries(value || {}).map(([key, val]) => ({
                                    label: `${labels[key] || key} (${val}s)`,
                                    value: val,
                                    color: colors[key] || '#0078D4',
                                    percentage: 0  // Sera calculé par le DonutChartWidget
                                }));

                                return {
                                    widgetId: widget.id,
                                    data: segments
                                };
                            } else {
                                // Pour MetricWidget: extraire value, targetValue et maxValue
                                let targetValue = null;
                                if (widget.config?.targetPath) {
                                    const targetParts = widget.config.targetPath.split('.');
                                    targetValue = analyticsProfile.reportConfig;
                                    
                                    for (const part of targetParts) {
                                        targetValue = targetValue?.[part];
                                    }
                                }
                                
                                let maxValue = null;
                                if (widget.config?.maxPath) {
                                    const maxParts = widget.config.maxPath.split('.');
                                    maxValue = analyticsProfile.reportConfig;
                                    
                                    for (const part of maxParts) {
                                        maxValue = maxValue?.[part];
                                    }
                                }
                                
                                // Retourner la valeur dans le format attendu par MetricWidget
                                return { 
                                    widgetId: widget.id, 
                                    data: { 
                                        value: value || 0,
                                        maxValue: maxValue,
                                        targetValue: targetValue
                                    } 
                                };
                            }
                        }
                    } catch (error) {
                        console.error(`Error loading reportConfig for widget ${widget.id}:`, error);
                        return { widgetId: widget.id, error: error.message };
                    }

                    // Si on arrive ici, c'est que analyticsProfile n'est pas encore chargé
                    console.warn(`⚠️ Analytics Profile not available for ${widget.id}, skipping for now`);
                    return { widgetId: widget.id, data: null };
                }

                if (!widget.queryId) {
                    return { widgetId: widget.id, data: null };
                }

                try {
                    // 🔹 Enrichir les filtres avec les thresholds du reportConfig
                    let enrichedFilters = { ...widgetFilters };
                    
                    // 1. Chercher le profile du scope actuel
                    let analyticsProfile = Object.values(analyticsProfiles).find(
                        profile => profile.scope === decodedScope
                    );
                    
                    // 2. Si pas de profile, chercher celui de "default" UNIQUEMENT pour les thresholds
                    if (!analyticsProfile) {
                        analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === "default"
                        );
                    }

                    let widgetWithThresholds = { ...widget };

                    // 3. Ajouter les thresholds depuis reportConfig (du scope actuel ou de "default")
                    if (analyticsProfile?.reportConfig?.thresholds) {
                        const thresholds = analyticsProfile.reportConfig.thresholds;
                        enrichedFilters = {
                            ...enrichedFilters,
                            quickAnswerThreshold: thresholds.quickAnswerThreshold,
                            standardAnswerThreshold: thresholds.standardAnswerThreshold,
                            delayedAnswerThreshold: thresholds.delayedAnswerThreshold,
                            quickDropThreshold: thresholds.quickDropThreshold
                        };
                    }

                    // Inject SLA threshold (seconds) from reportConfig if widget declares a slaSecondsPath
                    if (widget.config?.slaSecondsPath && analyticsProfile?.reportConfig) {
                        const pathParts = widget.config.slaSecondsPath.split('.');
                        let slaSeconds = analyticsProfile.reportConfig;
                        for (const part of pathParts) {
                            slaSeconds = slaSeconds?.[part];
                        }
                        if (slaSeconds !== undefined && slaSeconds !== null) {
                            widgetWithThresholds = {
                                ...widgetWithThresholds,
                                config: {
                                    ...widgetWithThresholds.config,
                                    slaThreshold: Number(slaSeconds)
                                }
                            };
                            console.log(`🎯 Injecting SLA threshold for ${widget.id}: ${slaSeconds}s`);
                        }
                    }

                    // 5. Le scope reste TOUJOURS celui demandé (pas de changement)

                    // Fetch data from API avec les filtres enrichis
                    const result = await fetchWidgetData(widgetWithThresholds, enrichedFilters, tenantId, token);
                    console.log(widgetWithThresholds, enrichedFilters, 'enrichedFilters for widget', widget.id);
                    // Si le widget a un targetPath ou maxPath dans reportConfig, les injecter
                    if (widget.config?.targetPath || widget.config?.maxPath) {
                        // 🔹 Trouver le profile analytics par le champ 'scope'
                        let analyticsProfile = Object.values(analyticsProfiles).find(
                            profile => profile.scope === decodedScope
                        );

                        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
                        if (!analyticsProfile) {
                            analyticsProfile = Object.values(analyticsProfiles).find(
                                profile => profile.scope === "default"
                            );
                        }

                        if (analyticsProfile && analyticsProfile.reportConfig) {
                            if (widget.config?.targetPath) {
                                const pathParts = widget.config.targetPath.split('.');
                                let targetValue = analyticsProfile.reportConfig;
                                for (const part of pathParts) {
                                    targetValue = targetValue?.[part];
                                }
                                console.log(`🎯 Widget ${widget.id} target from reportConfig:`, targetValue);
                                if (result && typeof result === 'object') {
                                    result.targetValue = targetValue;
                                }
                            }

                            if (widget.config?.maxPath) {
                                const maxParts = widget.config.maxPath.split('.');
                                let maxValue = analyticsProfile.reportConfig;
                                for (const part of maxParts) {
                                    maxValue = maxValue?.[part];
                                }
                                console.log(`📈 Widget ${widget.id} max from reportConfig:`, maxValue);
                                if (result && typeof result === 'object') {
                                    result.maxValue = maxValue;
                                }
                            }
                        }
                    }

                    // Le mapping UUID→Name sera fait dans chaque Widget via Redux hooks
                    return { widgetId: widget.id, data: result };
                } catch (error) {
                    return { widgetId: widget.id, error: error.message };
                }
            };

            // Séparer les widgets reportConfig des widgets API
            const reportConfigWidgets = dashboardConfig.widgets.filter(w =>
                w.config?.dataSource === 'reportConfig'
            );
            const apiWidgets = dashboardConfig.widgets.filter(w =>
                !w.config?.dataSource || w.config?.dataSource !== 'reportConfig'
            );

            // ÉTAPE 1: Charger les widgets reportConfig immédiatement (pas d'API call)
            const reportConfigResults = await Promise.all(reportConfigWidgets.map(loadWidget));

            // ÉTAPE 2: Charger les widgets API en parallèle (Cosmos DB supporte la concurrence)
            const results = [...reportConfigResults];
            const BATCH_SIZE = 4;

            for (let i = 0; i < apiWidgets.length; i += BATCH_SIZE) {
                const batch = apiWidgets.slice(i, i + BATCH_SIZE);

                const batchResults = await Promise.all(batch.map(loadWidget));
                results.push(...batchResults);
            }

            // Créer un objet map des données par widget ID
            const dataMap = {};
            results.forEach(({ widgetId, data, error }) => {
                dataMap[widgetId] = error ? { error } : data;
                // Log pour debug
                if (widgetId.includes('abandon') || widgetId.includes('talking') || widgetId.includes('waiting') || widgetId.includes('threshold')) {
                    console.log(`🗺️ Adding to widgetsData: ${widgetId}`, error ? { error } : data);
                }
            });

            setWidgetsData(dataMap);
        } catch (error) {
            // Error handled silently
        } finally {
            setIsLoadingWidgets(false);
        }
    // ⚠️ IMPORTANT: widgetInstances est utilisé dans loadWidget mais PAS dans les dépendances
    // pour éviter de recharger TOUS les widgets quand on modifie la config d'UN SEUL widget
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardConfig, appliedFilters, tenantId, token, currentScopeData, analyticsProfiles, decodedScope, selectedTimezone]);

    // Charger toutes les données des widgets en parallèle quand les filtres appliqués changent
    // ⚠️ widgetInstances est inclus pour détecter le chargement initial depuis Cosmos
    useEffect(() => {
        // 🔹 Ne pas recharger si on vient de sauvegarder un widget individuel
        if (isSavingWidgetRef.current) {
            isSavingWidgetRef.current = false;
            return;
        }
        
       
        
        // 🔹 Vérifications strictes avant de charger les widgets
        if (!appliedFilters || !tenantId || !dashboardConfig) {
            return;
        }

        // 🔹 Vérifier que les analytics profiles sont chargés
        if (!analyticsProfiles) {
            console.log('⏳ Waiting for analytics profiles to load...');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        let analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        );

        // 🔹 FALLBACK: Si pas de profile pour ce scope, utiliser celui de "default"
        if (!analyticsProfile) {
            analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === "default"
            );
        }


      

        loadAllWidgetsData();
    }, [appliedFilters, tenantId, selectedTab, dashboardConfig, analyticsProfiles, decodedScope, loadAllWidgetsData, widgetInstancesLoaded]);

    /**
     * Charger les données des widgets personnalisés dans les tabs custom
     * Ces widgets peuvent avoir leurs propres filtres (période personnalisée + filtres flow/queue/agent)
     */
    useEffect(() => {
        const loadCustomTabWidgetsData = async () => {
            // Charger uniquement si on est sur un tab custom ET qu'il a des widgets
            const isCustomTab = selectedTab !== 'dashboard' && selectedTab !== 'calls';
            if (!isCustomTab || !tenantId || !token || !analyticsProfiles) {
                return;
            }

            const tabWidgets = getWidgetInstancesForTab(selectedTab);
            if (tabWidgets.length === 0) {
                return;
            }

            // Charger chaque widget avec ses propres filtres
            const results = await Promise.all(tabWidgets.map(async (widgetInstance) => {
                try {
                    // Calculer les filtres pour ce widget spécifique
                    let widgetFilters;
                    
                    if (widgetInstance.config?.useGlobalFilters !== false) {
                        // Mode: FILTRES GLOBAUX du dashboard
                        widgetFilters = appliedFilters;
                    } else {
                        // Mode: FILTRES PERSONNALISÉS (tous optionnels)
                        const customPeriod = widgetInstance.config?.customPeriod;
                        const customFilters = widgetInstance.config?.customFilters || {};
                        
                        widgetFilters = {
                            scope: decodedScope,
                            timezone: selectedTimezone
                        };
                        
                        // Ajouter les dates SEULEMENT si une période est définie
                        if (customPeriod) {
                            const dateRange = calculateDateRangeFromPeriod(customPeriod);
                            widgetFilters.startDate = dateRange.startDate;
                            widgetFilters.endDate = dateRange.endDate;
                        }
                        
                        // Ajouter les filtres flow/queue/agent s'ils sont définis
                        if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                        if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                        if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                    }


                    // Charger les données avec fetchWidgetData
                    // Construire un widgetConfig complet à partir de l'instance et du catalogue
                    const catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetInstance.widgetId);
                    const originalWidgetConfig = widgetInstance.originalConfig || (dashboardConfig?.widgets && dashboardConfig.widgets.find(w => w.id === widgetInstance.widgetId));

                    const widgetConfig = {
                        queryId: widgetInstance.config?.queryId || originalWidgetConfig?.queryId || catalogWidget?.dataSource?.queryId,
                        type: widgetInstance.config?.type || originalWidgetConfig?.type || catalogWidget?.type,
                        filters: widgetInstance.config?.filters || originalWidgetConfig?.filters,
                        config: widgetInstance.config || originalWidgetConfig
                    };

                    const data = await fetchWidgetData(widgetConfig, widgetFilters, tenantId, token);

                    return {
                        widgetId: widgetInstance.widgetId,
                        data,
                        error: null
                    };
                } catch (error) {
                    console.error(`❌ Error loading widget ${widgetInstance.widgetId}:`, error);
                    return {
                        widgetId: widgetInstance.widgetId,
                        data: null,
                        error: error.message
                    };
                }
            }));

            // Mettre à jour widgetsData avec les nouvelles données
            const dataMap = {};
            results.forEach(({ widgetId, data, error }) => {
                dataMap[widgetId] = error ? { error } : data;
            });

            setWidgetsData(prev => ({
                ...prev,
                ...dataMap
            }));

         
        };

        loadCustomTabWidgetsData();
    }, [selectedTab, widgetInstances, appliedFilters, tenantId, token, analyticsProfiles, decodedScope, selectedTimezone, currentScopeData]);

    /**
     * Gérer le changement de la date de début
     */
    const handleStartDateChange = (date) => {
        setFilterValues(prev => ({
            ...prev,
            startDate: date
        }));
    };

    /**
     * Gérer le changement de la date de fin
     */
    const handleEndDateChange = (date) => {
        setFilterValues(prev => ({
            ...prev,
            endDate: date
        }));
    };

    /**
     * Normaliser une date à minuit UTC
     */
    const normalizeDate = (date) => {
        if (!date) return null;

        // Si c'est une Date object, extraire year/month/day en local puis créer en UTC
        const d = new Date(date);
        if (isNaN(d.getTime())) {
            return null;
        }

        // Extraire composants en timezone locale
        const year = d.getFullYear();
        const month = d.getMonth(); // 0-11
        const day = d.getDate();

        // Recréer en UTC (mois 0-11, donc pas besoin de +1)
        return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
    };

    /**
     * Appliquer les filtres sélectionnés
     */
    const handleApplyFilters = () => {
        if (!filterValues.startDate || !filterValues.endDate) {
            return;
        }

        // ✅ VALIDATION: Cosmos DB est rapide, on autorise jusqu'à 1 an (365 jours)
        const diffMs = new Date(filterValues.endDate) - new Date(filterValues.startDate);
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        const maxDays = 365; // 1 an max (Cosmos DB est ultra-rapide)

        if (diffDays > maxDays) {
            alert(t('DATE_RANGE_TOO_LARGE', { maxDays, diffDays }));
            return;
        }

        const startDateNormalized = normalizeDate(filterValues.startDate);
        const endDateNormalized = normalizeDate(filterValues.endDate);

        if (!startDateNormalized || !endDateNormalized) {
            return;
        }

        const filters = {
            startDate: startDateNormalized,
            endDate: endDateNormalized,
            scope: decodedScope, // IMPORTANT: Utiliser decodedScope pour partition pruning
            timezone: selectedTimezone, // Timezone pour conversion des heures
            ...dynamicFilters // Inclure flowKey, queueKey, agentKey
        };

        setAppliedFilters(filters);
    };

    /**
     * Gérer les changements de filtres dynamiques (flows/queues/agents)
     */
    const handleDynamicFilterChange = (filters) => {
        setDynamicFilters(filters);

        // Auto-apply filters when dynamic filters change
        if (filterValues.startDate && filterValues.endDate) {
            const startDateNormalized = normalizeDate(filterValues.startDate);
            const endDateNormalized = normalizeDate(filterValues.endDate);

            if (startDateNormalized && endDateNormalized) {
                const updatedFilters = {
                    startDate: startDateNormalized,
                    endDate: endDateNormalized,
                    scope: decodedScope,
                    timezone: selectedTimezone,
                    ...filters // Use the new filters directly
                };
                setAppliedFilters(updatedFilters);
            }
        }
    };

    /**
     * Charger l'enregistrement et la transcription pour un appel
     * Utilise directement les données de callsList (pas de service externe)
     * @param {string} callId - L'ID de l'appel
     * @param {string} recordingScopeId - Non utilisé, gardé pour compatibilité
     */
    const loadRecordingAndTranscription = async (callId, recordingScopeId) => {
        try {
            // ✅ CHECK CACHE LOCAL : Si déjà chargé, ne rien faire
            if (recordingUrls[callId] !== undefined && transcriptions[callId] !== undefined) {
                console.log(`✅ Using cached data for call ${callId}`);
                return;
            }

            // Chercher les données directement dans callsList
            const callData = callsList.find(call => call.callId === callId);
            
            if (!callData) {
                console.log(`⚠️ Call ${callId} not found in callsList`);
                setRecordingUrls(prev => ({ ...prev, [callId]: null }));
                setTranscriptions(prev => ({ ...prev, [callId]: null }));
                return;
            }

            console.log(`🔍 Found call data for ${callId}:`, {
                hasRecordingBlobUrl: !!callData.recordingBlobUrl,
                isTranscriptionActive: callData.isTranscriptionActive,
                hasTranscriptionText: !!callData.transcriptionText
            });

            // 🎧 CHARGER L'ENREGISTREMENT AUDIO depuis callsList
            if (recordingUrls[callId] === undefined) {
                const recordingUrl = callData.recordingBlobUrl || null;
                
                if (recordingUrl) {
                    setRecordingUrls(prev => ({ ...prev, [callId]: recordingUrl }));
                    console.log(`🎧 Recording URL loaded from callsList for call ${callId}`);
                } else {
                    setRecordingUrls(prev => ({ ...prev, [callId]: null }));
                    console.log(`⚠️ No recordingBlobUrl in callsList for call ${callId}`);
                }
            }
            
            // 📝 CHARGER LA TRANSCRIPTION depuis callsList
            if (transcriptions[callId] === undefined) {
                const transcriptionText = callData.transcriptionText || null;
                const isTranscriptionActive = callData.isTranscriptionActive || false;
                
                if (isTranscriptionActive && transcriptionText) {
                    setTranscriptions(prev => ({ ...prev, [callId]: transcriptionText }));
                    console.log(`📝 Transcription loaded from callsList for call ${callId}`);
                } else {
                    setTranscriptions(prev => ({ ...prev, [callId]: null }));
                    console.log(`⚠️ No transcription in callsList for call ${callId}`);
                }
            }
            
        } catch (error) {
            console.error(`❌ Error loading recording/transcription for call ${callId}:`, error);
            setRecordingUrls(prev => ({ ...prev, [callId]: null }));
            setTranscriptions(prev => ({ ...prev, [callId]: null }));
        }
    };

    /**
     * Charger la liste complète des appels
     */
    const loadCallsList = async () => {
        if (!appliedFilters || !token || !tenantId) {
            return;
        }

        setIsLoadingCalls(true);
        try {
            const { executeAnalyticsQuery } = await import('../../Services/AnalyticsService');

            const result = await executeAnalyticsQuery(
                tenantId,
                'all-calls-list',
                appliedFilters,
                token
            );

            console.log('📞 Calls List - Result from Analytics Service:', result);
            console.log('📞 Calls List - Data array:', result.data);
            console.log('📞 Calls List - Number of calls:', result.data?.length);
            if (result.data && result.data.length > 0) {
                console.log('📞 Calls List - First call COMPLETE OBJECT:', JSON.stringify(result.data[0], null, 2));
                console.log('📞 Calls List - Available fields:', Object.keys(result.data[0]));
                
                // Vérifier les champs liés à l'audio/recording
                const firstCall = result.data[0];
                console.log('🎵 Audio/Recording fields check:');
                console.log('  - recordingUrl:', firstCall.recordingUrl);
                console.log('  - recording:', firstCall.recording);
                console.log('  - audioUrl:', firstCall.audioUrl);
                console.log('  - recordingBlobUrl:', firstCall.recordingBlobUrl);
                console.log('  - blobUrl:', firstCall.blobUrl);
                console.log('  - mediaUrl:', firstCall.mediaUrl);
                console.log('  - flowId:', firstCall.flowId);
                console.log('  - agentId:', firstCall.agentId);
                
                // Vérifier les champs liés à la transcription
                console.log('📝 Transcription fields check:');
                console.log('  - transcription:', firstCall.transcription);
                console.log('  - transcriptionText:', firstCall.transcriptionText);
                console.log('  - transcript:', firstCall.transcript);
                
                // Log complet de tous les appels pour analyse
                console.log('📋 ALL CALLS LIST DATA:', result.data);
            }

            setCallsList(result.data || []);
        } catch (error) {
            console.error('❌ Error loading calls list:', error);
            setCallsList([]);
        } finally {
            setIsLoadingCalls(false);
        }
    };

    /**
     * Gérer les filtres de colonnes du Calls List
     */
    const handleCallsFilterChange = React.useCallback((columnKey, value) => {
        setCallsFilters((prev) => ({ ...prev, [columnKey]: value }));
    }, []);

    const toggleCallsFilterVisibility = React.useCallback((column) => {
        setCallsFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
    }, []);

    /**
     * Filtrer les appels en fonction des filtres de colonnes et ajouter les lignes expandées
     */
    const filteredCallsList = React.useMemo(() => {
        const filtered = callsList.filter((call) => {
            return Object.entries(callsFilters).every(([key, value]) => {
                if (!value) return true;
                const cellValue = call[key];
                if (cellValue === null || cellValue === undefined) return false;
                return String(cellValue).toLowerCase().includes(value.toLowerCase());
            });
        });

        // Insérer les lignes expandées
        const result = [];
        filtered.forEach(call => {
            result.push(call);
            if (expandedRows[call.callId]) {
                result.push({
                    ...call,
                    isExpandedRow: true,
                    uniqueKey: `${call.callId}-expanded`
                });
            }
        });
        return result;
    }, [callsList, callsFilters, expandedRows]);

    /**
     * Charger la liste des appels quand l'onglet change
     */
    useEffect(() => {
        if (selectedTab === 'calls' && appliedFilters) {
            loadCallsList();
        }
    }, [selectedTab, appliedFilters]);

    /**
     * Helper pour rendre le TextField avec icône de filtre
     */
    const renderFilterTextField = (columnKey, minWidth = 150) => (
        <div style={{ position: 'relative', minWidth, flex: 1, display: 'flex', alignItems: 'center' }}>
          <TextField
                value={callsFilters[columnKey] || ''}
                onChange={(e, value) => handleCallsFilterChange(columnKey, value)}
                placeholder={t('FILTER')}
                styles={{ 
                    root: { width: '90%' }, 
                    fieldGroup: { 
                        height: 23, 
                        border: '1px solid #d1d1d1',
                        borderRadius: 4,
                        paddingLeft: 3
                    },
                    field: { paddingLeft: 0 }
                }}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );

    /**
     * Colonnes du DetailsList avec filtres - useMemo pour forcer le re-render
     */
    const callsListColumns = React.useMemo(() => {
        return [
            {
                key: 'expand',
                name: '',
                fieldName: '',
                minWidth: 20,
                maxWidth: 20,
                onRender: (item) => {
                    if (item.isExpandedRow) {
                        return null;
                    }
                     console.log(`🔀 Toggling expand for call ${item}:`);
                               
                    const ChevronIcon = expandedRows[item.callId] ? ChevronDown20Regular : ChevronRight20Regular;
                    return (
                        <div 
                            onClick={() => {
                                const newExpandedState = { ...expandedRows, [item.callId]: !expandedRows[item.callId] };
                                setExpandedRows(newExpandedState);
                                // Si on expand et qu'on n'a pas encore l'URL, la charger
                                if (!expandedRows[item.callId] && !recordingUrls[item.callId]) {
                                     // Utiliser  || item.agentId || item. comme recordingScopeId (c'est le PartitionKey dans Cosmos)
                                     console.log(`📡 Loading recording/transcription for call ${item.callId} with scope ${item.flowId || item.agentId}`);
                                    loadRecordingAndTranscription(item.callId, item.flowId || item.agentId || item.callerNumber );
                                }
                            }}
                            style={{ 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '42px',
                                minHeight: '42px'
                            }}
                            title={expandedRows[item.callId] ? 'Collapse' : 'Expand'}
                        >
                            <ChevronIcon style={{ fontSize: '20px',fontWeight: 'bold', color: 'rgb(91, 95, 199)' }} />
                        </div>
                    );
                }
            },
            { 
                key: 'callId', 
                name: t('CALL_ID'), 
                fieldName: 'callId', 
                minWidth: callsFilterVisible.callId ? 250 : 230, 
                maxWidth: callsFilterVisible.callId ? 300 : 250,
                onRender: (item) => {
                    if (item.isExpandedRow) {
                        return (
                            <div style={{ 
                                padding: '10px',
                              
                                marginLeft: '-20px',
                                marginRight: '-20px',
                                width: 'calc(100% + 40px)'
                            }}>
                                <div style={{ 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px'
                                }}>
                                    {/* Affichage de l'enregistrement audio */}
                                    {item.callId in recordingUrls ? (
                                        recordingUrls[item.callId] ? (
                                            <div>
                                                <div style={{ 
                                                    fontWeight: 600, 
                                                    marginBottom: '8px',
                                                    color: '#323130'
                                                }}>{t('RECORDING')}</div>
                                                <audio controls style={{ width: '100%', maxWidth: '600px' }}>
                                                    <source src={recordingUrls[item.callId]} type="audio/wav" />
                                                    {t('BROWSER_NOT_SUPPORT_AUDIO')}
                                                </audio>
                                            </div>
                                        ) : (
                                            <MessageBar messageBarType={MessageBarType.info}>
                                                {t('NO_RECORDING_AVAILABLE')}
                                            </MessageBar>
                                        )
                                    ) : (
                                        <div style={{ 
                                            padding: '12px',
                                            backgroundColor: '#fff4ce',
                                            borderLeft: '3px solid #ffb900',
                                            color: '#323130'
                                        }}>
                                            {t('LOADING_RECORDING')}
                                        </div>
                                    )}
                                    {/* Affichage de la transcription */}
                                    {item.callId in transcriptions ? (
                                        transcriptions[item.callId] ? (
                                            <div>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: '12px',
                                                    marginBottom: '8px'
                                                }}>
                                                    <span 
                                                        style={{ 
                                                            fontWeight: 600, 
                                                            color: '#323130'
                                                        }}
                                                    >
                                                        {t('TRANSCRIPTION')}
                                                    </span>
                                                    <AddSquare28Regular
                                                        style={{ 
                                                            width: '20px',
                                                            height: '20px',
                                                            cursor: 'pointer',
                                                            color: 'rgb(91, 95, 199)'
                                                        }}
                                                        onClick={() => {
                                                            setSelectedTranscription({ 
                                                                callId: item.callId, 
                                                                text: transcriptions[item.callId] 
                                                            });
                                                            setShowTranscriptionPanel(true);
                                                        }}
                                                        title={t('SHOW_TRANSCRIPTION')}
                                                    />
                                                </div>
                                             
                                            </div>
                                        ) : (
                                            <MessageBar messageBarType={MessageBarType.info}>
                                                {t('NO_TRANSCRIPTION_AVAILABLE')}
                                            </MessageBar>
                                        )
                                    ) : (
                                        <div style={{ 
                                            padding: '12px',
                                            backgroundColor: '#f3f2f1',
                                            borderLeft: '3px solid #8a8886',
                                            color: '#323130'
                                        }}>
                                            {t('LOADING_TRANSCRIPTION')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    return <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px', paddingLeft: '8px' }}>{item.callId}</div>;
                },
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('CALL_ID')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callId ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callId');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callId && renderFilterTextField('callId', 100)}
                    </div>
                )
            },
            { 
                key: 'timestamp', 
                name: t('START_TIME'), 
                fieldName: 'timestamp', 
                minWidth: callsFilterVisible.timestamp ? 250 : 130, 
                maxWidth: callsFilterVisible.timestamp ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.timestamp}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('START_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.timestamp ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('timestamp');
                                }}
                            />
                        </div>
                        {callsFilterVisible.timestamp && renderFilterTextField('timestamp', 100)}
                    </div>
                )
            },
            { 
                key: 'direction', 
                name: t('DIRECTION'), 
                fieldName: 'direction', 
                minWidth: callsFilterVisible.direction ? 250 : 100, 
                maxWidth: callsFilterVisible.direction ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.direction}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('DIRECTION')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.direction ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('direction');
                                }}
                            />
                        </div>
                        {callsFilterVisible.direction && renderFilterTextField('direction', 100)}
                    </div>
                )
            },
            { 
                key: 'callerNumber', 
                name: t('CALLER'), 
                fieldName: 'callerNumber', 
                minWidth: callsFilterVisible.callerNumber ? 250 : 100, 
                maxWidth: callsFilterVisible.callerNumber ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.callerNumber}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('CALLER')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callerNumber ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callerNumber');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callerNumber && renderFilterTextField('callerNumber', 100)}
                    </div>
                )
            },
            { 
                key: 'callerType', 
                name: t('TYPE'), 
                fieldName: 'callerType', 
                minWidth: callsFilterVisible.callerType ? 250 : 100, 
                maxWidth: callsFilterVisible.callerType ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.callerType}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('TYPE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.callerType ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('callerType');
                                }}
                            />
                        </div>
                        {callsFilterVisible.callerType && renderFilterTextField('callerType', 100)}
                    </div>
                )
            },
            {
                key: 'wasTransferred',
                name: t('TRANSFERRED'),
                fieldName: 'wasTransferred',
                minWidth: callsFilterVisible.wasTransferred ? 250 : 100,
                maxWidth: callsFilterVisible.wasTransferred ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.wasTransferred ? '✅ ' + t('YES') : '—'}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('TRANSFERRED')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.wasTransferred ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('wasTransferred');
                                }}
                            />
                        </div>
                        {callsFilterVisible.wasTransferred && renderFilterTextField('wasTransferred', 100)}
                    </div>
                )
            },
            { 
                key: 'agentName', 
                name: t('AGENT'), 
                fieldName: 'agentName', 
                minWidth: callsFilterVisible.agentName ? 250 : 100,
                maxWidth: callsFilterVisible.agentName ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.agentName}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('AGENT')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.agentName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('agentName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.agentName && renderFilterTextField('agentName', 100)}
                    </div>
                )
            },
            { 
                key: 'queueName', 
                name: t('QUEUE'), 
                fieldName: 'queueName', 
                minWidth: callsFilterVisible.queueName ? 250 : 100,
                maxWidth: callsFilterVisible.queueName ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.queueName}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('QUEUE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.queueName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('queueName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.queueName && renderFilterTextField('queueName', 100)}
                    </div>
                )
            },
            { 
                key: 'flowName', 
                name: t('FLOW'), 
                fieldName: 'flowName', 
                minWidth: callsFilterVisible.flowName ? 250 : 100,
                maxWidth: callsFilterVisible.flowName ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.flowName}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('FLOW')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.flowName ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('flowName');
                                }}
                            />
                        </div>
                        {callsFilterVisible.flowName && renderFilterTextField('flowName', 100)}
                    </div>
                )
            },
            { 
                key: 'waitTime', 
                name: t('WAIT_TIME'), 
                fieldName: 'waitTime', 
                minWidth: callsFilterVisible.waitTime ? 250 : 100, 
                maxWidth: callsFilterVisible.waitTime ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.waitTime}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('WAIT_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.waitTime ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('waitTime');
                                }}
                            />
                        </div>
                        {callsFilterVisible.waitTime && renderFilterTextField('waitTime', 100)}
                    </div>
                )
            },
            { 
                key: 'duration', 
                name: t('DURATION'), 
                fieldName: 'duration', 
                minWidth: callsFilterVisible.duration ? 250 : 100, 
                maxWidth: callsFilterVisible.duration ? 300 : 150,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.duration}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('DURATION')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.duration ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('duration');
                                }}
                            />
                        </div>
                        {callsFilterVisible.duration && renderFilterTextField('duration', 100)}
                    </div>
                )
            },
            { 
                key: 'alertTime', 
                name: t('AGENT_ALERT_TIME'), 
                fieldName: 'alertTime', 
                minWidth: callsFilterVisible.alertTime ? 250 : 150, 
                maxWidth: callsFilterVisible.alertTime ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.alertTime}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('AGENT_ALERT_TIME')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.alertTime ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('alertTime');
                                }}
                            />
                        </div>
                          {callsFilterVisible.alertTime && renderFilterTextField('alertTime', 100)}
                
                      
    
                    </div>
                )
            },
            {
                key: 'queuedTimes',
                name: t('QUEUE_ENTRIES'),
                fieldName: 'queuedTimes',
                minWidth: callsFilterVisible.queuedTimes ? 250 : 150,
                maxWidth: callsFilterVisible.queuedTimes ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.queuedTimes}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('QUEUE_ENTRIES')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.queuedTimes ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('queuedTimes');
                                }}
                            />
                        </div>
                        {callsFilterVisible.queuedTimes && renderFilterTextField('queuedTimes', 100)}
                    </div>
                )
            },
            {
                key: 'agentsAlerted',
                name: t('AGENTS_ALERTED'),
                fieldName: 'agentsAlerted',
                minWidth: callsFilterVisible.agentsAlerted ? 250 : 150,
                maxWidth: callsFilterVisible.agentsAlerted ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.agentsAlerted ?? 0}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('AGENTS_ALERTED')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.agentsAlerted ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('agentsAlerted');
                                }}
                            />
                        </div>
                        {callsFilterVisible.agentsAlerted && renderFilterTextField('agentsAlerted', 100)}
                    </div>
                )
            },
            {
                key: 'queuePath',
                name: t('QUEUE_PATH'),
                fieldName: 'queuePath',
                minWidth: callsFilterVisible.queuePath ? 350 : 250,
                maxWidth: callsFilterVisible.queuePath ? 500 : 400,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.queuePath || 'N/A'}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('QUEUE_PATH')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.queuePath ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('queuePath');
                                }}
                            />
                        </div>
                        {callsFilterVisible.queuePath && renderFilterTextField('queuePath', 100)}
                    </div>
                )
            },
            { 
                key: 'terminationReason', 
                name: t('END_REASON'), 
                fieldName: 'terminationReason', 
                minWidth: callsFilterVisible.terminationReason ? 250 : 150, 
                maxWidth: callsFilterVisible.terminationReason ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.terminationReason}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('END_REASON')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.terminationReason ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('terminationReason');
                                }}
                            />
                        </div>
                        {callsFilterVisible.terminationReason && renderFilterTextField('terminationReason', 100)}
                    </div>
                )
            },
            { 
                key: 'interactionType', 
                name: t('INTERACTION_TYPE'), 
                fieldName: 'interactionType', 
                minWidth: callsFilterVisible.interactionType ? 250 : 150, 
                maxWidth: callsFilterVisible.interactionType ? 300 : 200,
                onRender: (item) => item.isExpandedRow ? null : <div style={{ display: 'flex', alignItems: 'center', height: '42px', minHeight: '42px' }}>{item.interactionType}</div>,
                onRenderHeader: () => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                            <span>{t('INTERACTION_TYPE')}</span>
                            <CiFilter
                                style={{ cursor: 'pointer', fontSize: '16px', color: callsFilterVisible.interactionType ? '#0078d4' : '#605e5c' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCallsFilterVisibility('interactionType');
                                }}
                            />
                        </div>
                        {callsFilterVisible.interactionType && renderFilterTextField('interactionType', 100)}
                    </div>
                )
            }
        ];
    }, [callsFilterVisible, callsFilters, t, handleCallsFilterChange, toggleCallsFilterVisibility, renderFilterTextField, expandedRows]);

    /**
     * Exporter la liste des appels vers Excel (async avec Blob Storage)
     */
    const handleExportToExcel = async () => {
        if (!appliedFilters || !token || !tenantId) {
            return;
        }

        setIsExportingExcel(true);
        setShowExportDialog(true);
        setExportProgress(0);
        setExportMessage(t('EXPORT_STARTING'));
        setExportDownloadUrl(null);

        try {
            // Start async export job — inject scope so Cosmos can use the HPK partition
            const exportFilters = { ...appliedFilters, scope: decodedScope };
            const startResult = await startExcelExport(tenantId, exportFilters, token);

            if (!startResult.success) {
                throw new Error(startResult.error);
            }

            setExportMessage(t('EXPORT_IN_PROGRESS'));

            // Poll for status
            const finalStatus = await pollExportStatus(
                startResult.jobId,
                token,
                (status) => {
                    setExportProgress(status.progress || 0);
                    setExportMessage(status.message || 'En cours...');
                }
            );

            setExportProgress(100);
            setExportMessage(`${t('EXPORT_COMPLETED')} ${finalStatus.totalRecords} ${t('RECORDS_EXPORTED')}`);
            setExportDownloadUrl(finalStatus.downloadUrl);

            // Auto-download — fetch the blob and trigger a local download
            // (the `download` HTML attribute is ignored for cross-origin URLs like Azure Blob)
            try {
                const blobResponse = await fetch(finalStatus.downloadUrl);
                if (!blobResponse.ok) throw new Error(`Blob fetch failed: ${blobResponse.status}`);
                const blob = await blobResponse.blob();
                const localUrl = URL.createObjectURL(blob);

                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10);
                const filename = `analytics-export_${decodedScope}_${dateStr}.xlsx`;

                const link = document.createElement('a');
                link.href = localUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Release blob memory after a short delay to ensure the download started
                setTimeout(() => URL.revokeObjectURL(localUrl), 1000);
            } catch (downloadErr) {
                console.error('Auto-download failed, user can use the manual button:', downloadErr);
            }

        } catch (error) {
            setExportMessage(`${t('EXPORT_ERROR')}: ${error.message}`);
        } finally {
            setIsExportingExcel(false);
        }
    };

    /**
     * Fermer la dialog d'export
     */
    const closeExportDialog = () => {
        setShowExportDialog(false);
        setExportProgress(0);
        setExportMessage('');
        setExportDownloadUrl(null);
    };

    /**
     * Exporter le dashboard en PDF
     */
    const handleExportPDF = async () => {
        setIsExporting(true);
        try {
            const startDate = filterValues.startDate?.toLocaleDateString('en-US') || '';
            const endDate = filterValues.endDate?.toLocaleDateString('en-US') || '';
            const filename = `analytics-report_${decodedScope}_${startDate.replace(/\//g, '-')}_to_${endDate.replace(/\//g, '-')}.pdf`;

            await exportToPDF('dashboard-container', filename, {
                scopeName: decodedScope
            });
        } catch (error) {
            // Error handled silently
        } finally {
            setIsExporting(false);
        }
    };

    /**
     * Dashboard positions now saved via Analytics Profile (saveDashboardLayoutToBackend)
     * Old Portal Settings method removed - all saves go through Analytics Profile service
     */

    /**
     * handleResetLayout removed - Reset button no longer in UI
     */

    /**
     * Ouvrir le catalogue et sélectionner un widget pour l'ajouter
     */
    const handleOpenCatalog = () => {
        setIsCatalogOpen(true);
    };

    /**
     * Sélectionner un widget depuis le catalogue pour l'instancier
     */
    const handleSelectWidgetFromCatalog = (widgetInfo) => {
        setSelectedWidgetInfo(widgetInfo);
        setEditingInstance(null);
        setIsCatalogOpen(false);
        setIsInstanceConfigOpen(true);
    };

    /**
     * Sauvegarder une instance de widget
     */
    const handleSaveWidgetInstance = (instance) => {
        const updatedWidgets = {
            ...widgetInstances,
            [instance.id]: {
                ...instance,
                updatedAt: new Date().toISOString()
            }
        };
        setWidgetInstances(updatedWidgets);
    };

    /**
     * Éditer une instance de widget existante
     */
    const handleEditWidgetInstance = (instanceId) => {
        const instance = widgetInstances[instanceId];
        if (!instance) return;

        const widgetInfo = WIDGET_CATALOG.find(w => w.id === instance.widgetId);
        setSelectedWidget(widgetInfo);  // Utiliser setSelectedWidget pour le panel
        setEditingInstance(instance);
        setShowConfigPanel(true);  // Ouvrir le panel à droite au lieu du dialog
    };

    /**
     * Supprimer une instance de widget d'un tab
     */
    const handleDeleteWidgetInstance = (instanceId) => {
        const updatedWidgets = { ...widgetInstances };
        delete updatedWidgets[instanceId];
        setWidgetInstances(updatedWidgets);
    };

    /**
     * New handlers for drag and drop from catalog panel
     */

    // Handle widget drag start from catalog
    const handleWidgetDragStart = (widget) => {
        setDraggedWidget(widget);
    };

    // Handle widget click from catalog (alternative to drag)
    const handleWidgetClick = (widget) => {
        setSelectedWidget(widget);
        setEditingInstance(null);
        setShowConfigPanel(true);
    };

    /**
     * Recharger les données d'un seul widget après modification de sa config
     */
    const reloadSingleWidgetData = async (widgetData) => {
        try {
            
            // Trouver la config complète du widget depuis dashboardConfig
            const widgetConfig = dashboardConfig?.widgets?.find(w => w.id === widgetData.widgetId);
            
            // Calculer les filtres pour ce widget
            let widgetFilters;
            
            if (widgetData.config?.useGlobalFilters !== false) {
                // Mode: FILTRES GLOBAUX
                widgetFilters = appliedFilters;
            } else {
                // Mode: FILTRES PERSONNALISÉS
                const customPeriod = widgetData.config?.customPeriod;
                const customFilters = widgetData.config?.customFilters || {};
                
                widgetFilters = {
                    scope: decodedScope,
                    timezone: selectedTimezone
                };
                
                if (customPeriod) {
                    const dateRange = calculateDateRangeFromPeriod(customPeriod);
                    widgetFilters.startDate = dateRange.startDate;
                    widgetFilters.endDate = dateRange.endDate;
                }
                
                if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
                
            }
            
            // 🔹 Vérifier si le widget utilise reportConfig comme source
            let data;
            if (widgetConfig?.config?.dataSource === 'reportConfig' && widgetConfig?.config?.dataPath) {
                // Charger depuis reportConfig
                let analyticsProfile = Object.values(analyticsProfiles).find(
                    profile => profile.scope === decodedScope
                );
                
                if (!analyticsProfile) {
                    analyticsProfile = Object.values(analyticsProfiles).find(
                        profile => profile.scope === "default"
                    );
                }
                
                if (analyticsProfile && analyticsProfile.reportConfig) {
                    // Extraire la valeur depuis le reportConfig
                    const pathParts = widgetConfig.config.dataPath.split('.');
                    let value = analyticsProfile.reportConfig;
                    
                    for (const part of pathParts) {
                        value = value?.[part];
                    }
                    
                    // Pour les widgets DonutChart, convertir l'objet en segments
                    if (widgetConfig.type === WidgetTypes.DONUT) {
                        const colors = widgetConfig.config.colors || {};
                        const labels = widgetConfig.config.labels || {};
                        
                        data = Object.entries(value || {}).map(([key, val]) => ({
                            label: `${labels[key] || key} (${val}s)`,
                            value: val,
                            color: colors[key] || '#0078D4',
                            percentage: 0
                        }));
                    } else {
                        // Pour les METRIC widgets, extraire aussi targetValue et maxValue
                        let targetValue = null;
                        if (widgetConfig.config?.targetPath) {
                            const targetParts = widgetConfig.config.targetPath.split('.');
                            targetValue = analyticsProfile.reportConfig;
                            for (const part of targetParts) {
                                targetValue = targetValue?.[part];
                            }
                        }
                        
                        let maxValue = null;
                        if (widgetConfig.config?.maxPath) {
                            const maxParts = widgetConfig.config.maxPath.split('.');
                            maxValue = analyticsProfile.reportConfig;
                            for (const part of maxParts) {
                                maxValue = maxValue?.[part];
                            }
                        }
                        
                        data = { 
                            value: value || 0,
                            maxValue: maxValue,
                            targetValue: targetValue
                        };
                    }
                }
            } else if (widgetConfig?.queryId) {
                // Charger depuis l'API SEULEMENT si le widget a un queryId
                data = await fetchWidgetData(
                    widgetConfig,      // Passer la config complète du widget
                    widgetFilters,     // Les filtres calculés
                    tenantId,
                    token
                );
            } else {
                // Pas de queryId ni reportConfig: pas de données à charger
                data = null;
            }
            
            // Mettre à jour uniquement ce widget dans widgetsData
            setWidgetsData(prev => ({
                ...prev,
                [widgetData.widgetId]: data
            }));
            
        } catch (error) {
            console.error('❌ Error reloading widget data:', error);
        }
    };

    // Handle config panel save
    const handleConfigSave = async (widgetData) => {
        const isNewWidget = !widgetInstances[widgetData.id];
       
        // Mettre à jour widgetInstances AVANT de sauvegarder
        const updatedWidgetData = {
            ...widgetData,
            updatedAt: new Date().toISOString()
        };
        
        // ✅ Créer le nouveau state AVANT de sauvegarder
        const updatedWidgetInstances = {
            ...widgetInstances,
            [widgetData.id]: updatedWidgetData
        };
        
      
        // ✅ Marquer qu'on est en train de sauvegarder pour éviter rechargement complet
        isSavingWidgetRef.current = true;
        
        // Mettre à jour le state
        setWidgetInstances(updatedWidgetInstances);

        // Sauvegarder dans Cosmos avec le state explicite pour éviter closure stale
     
        await saveDashboardLayoutToBackend(updatedWidgetInstances);  // ← Passer explicitement le state
       
        await reloadSingleWidgetData(updatedWidgetData);  // ← Utiliser updatedWidgetData au lieu de widgetData
        
        // Si c'est un nouveau widget, initialiser sa position
        if (isNewWidget) {
            const tabId = widgetData.tabId;
            const existingPositions = tabLayouts[tabId] || {};

            // Calculer une position intelligente pour le nouveau widget
            const existingWidgets = Object.values(existingPositions);
            const cols = 12; // Nombre de colonnes dans le grid
            const defaultWidth = 4; // Largeur par défaut du widget
            const defaultHeight = 4; // Hauteur par défaut

            // Créer une grille pour voir les espaces occupés
            let maxY = 0;
            const occupiedSpaces = {};

            existingWidgets.forEach(pos => {
                const bottom = pos.y + pos.h;
                if (bottom > maxY) maxY = bottom;

                // Marquer les espaces occupés
                for (let y = pos.y; y < pos.y + pos.h; y++) {
                    if (!occupiedSpaces[y]) occupiedSpaces[y] = [];
                    for (let x = pos.x; x < pos.x + pos.w; x++) {
                        occupiedSpaces[y].push(x);
                    }
                }
            });

            // Chercher le premier espace libre
            let foundPosition = null;
            for (let y = 0; y <= maxY && !foundPosition; y++) {
                for (let x = 0; x <= cols - defaultWidth; x++) {
                    // Vérifier si l'espace est libre
                    let isFree = true;
                    for (let dy = 0; dy < defaultHeight && isFree; dy++) {
                        const row = occupiedSpaces[y + dy] || [];
                        for (let dx = 0; dx < defaultWidth; dx++) {
                            if (row.includes(x + dx)) {
                                isFree = false;
                                break;
                            }
                        }
                    }

                    if (isFree) {
                        foundPosition = { x, y };
                        break;
                    }
                }
            }

            // Si aucun espace trouvé, mettre en bas
            if (!foundPosition) {
                foundPosition = { x: 0, y: maxY };
            }

            // Position finale pour le nouveau widget
            const newPosition = {
                x: foundPosition.x,
                y: foundPosition.y,
                w: defaultWidth,
                h: defaultHeight
            };

            const updatedLayouts = {
                ...tabLayouts,
                [tabId]: {
                    ...existingPositions,
                    [widgetData.id]: newPosition
                }
            };

            setTabLayouts(updatedLayouts);

            const updatedWidgetsPositions = {
                ...tabWidgetsPositions,
                [tabId]: {
                    ...(tabWidgetsPositions[tabId] || {}),
                    [widgetData.id]: newPosition
                }
            };
            setTabWidgetsPositions(updatedWidgetsPositions);
        }

        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };

    // Handle config panel close
    const handleConfigClose = () => {
        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };

    // Handle config panel delete
    const handleConfigDelete = (instanceId) => {
        handleDeleteWidgetInstance(instanceId);
        setShowConfigPanel(false);
        setSelectedWidget(null);
        setEditingInstance(null);
    };

    // Handle drop on dashboard area
    const handleDashboardDrop = (e) => {
        e.preventDefault();
        setDropZoneActive(false);

        if (!draggedWidget) return;

        // Créer une instance du widget avec configuration par défaut
        const newWidgetInstance = {
            id: `widget-instance-${Date.now()}`,
            widgetId: draggedWidget.id,
            name: draggedWidget.name,
            tabId: selectedTab,
            config: {
                showTitle: true,
                showBorder: true
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        // Ajouter l'instance
        const updatedInstances = {
            ...widgetInstances,
            [newWidgetInstance.id]: newWidgetInstance
        };
        setWidgetInstances(updatedInstances);

        // Calculer la position pour le nouveau widget
        const tabId = selectedTab;
        const existingPositions = tabLayouts[tabId] || {};
        const existingWidgets = Object.values(existingPositions);
        const defaultWidth = 6; // Demi-ligne (6 colonnes sur 12)
        const defaultHeight = draggedWidget.defaultSize?.h || 4;

        // Calculer la position : alterner entre colonne 0 et colonne 6
        const widgetCount = existingWidgets.length;
        const isLeftColumn = widgetCount % 2 === 0; // Pair = gauche, Impair = droite
        const rowIndex = Math.floor(widgetCount / 2); // Numéro de ligne

        // Calculer le Y maximum pour cette ligne
        let maxYForRow = rowIndex * defaultHeight;
        if (!isLeftColumn) {
            // Pour la colonne de droite, vérifier si la colonne de gauche existe et ajuster Y
            const leftColumnWidget = existingWidgets[widgetCount - 1];
            if (leftColumnWidget) {
                maxYForRow = leftColumnWidget.y;
            }
        }

        const newPosition = {
            x: isLeftColumn ? 0 : 6, // Colonne 0 ou 6
            y: maxYForRow,
            w: defaultWidth,
            h: defaultHeight
        };

        const updatedLayouts = {
            ...tabLayouts,
            [tabId]: {
                ...existingPositions,
                [newWidgetInstance.id]: newPosition
            }
        };
        setTabLayouts(updatedLayouts);

        const updatedWidgetsPositions = {
            ...tabWidgetsPositions,
            [tabId]: {
                ...(tabWidgetsPositions[tabId] || {}),
                [newWidgetInstance.id]: newPosition
            }
        };
        setTabWidgetsPositions(updatedWidgetsPositions);

        setDraggedWidget(null);
    };

    // Handle drag over dashboard area
    const handleDashboardDragOver = (e) => {
        e.preventDefault();
        setDropZoneActive(true);
    };

    // Handle drag leave dashboard area
    const handleDashboardDragLeave = (e) => {
        e.preventDefault();
        setDropZoneActive(false);
    };

    // Edit existing widget instance
    const handleEditWidget = (instanceId) => {
        const instance = widgetInstances[instanceId];
        if (!instance) return;

        const widgetInfo = WIDGET_CATALOG.find(w => w.id === instance.widgetId);
        setSelectedWidget(widgetInfo);
        setEditingInstance(instance);
        setShowConfigPanel(true);
    };

    /**
     * Initialiser le layout avec les positions par défaut et sauvegarder immédiatement
     */
    const initializeDefaultLayout = useCallback(async () => {
        if (!tenantId || !token || !decodedScope || !analyticsProfiles) {
            console.warn('⚠️ Cannot initialize default layout: missing params');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        const analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        );

        if (!analyticsProfile) {
            console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
            return;
        }

        try {
            console.log('🎬 Initializing default dashboard layout with DEFAULT_WIDGET_POSITIONS');
            console.log('⚠️ This only happens on first load or when no positions exist in Cosmos');

            // 🔒 Récupérer les tabs existants pour PRÉSERVER les custom tabs
            const existingResponse = await getDashboardLayout(tenantId, analyticsProfile.id, token);
            const existingTabs = (existingResponse?.success && existingResponse?.data?.tabs) || {};

            // Ne garder que les custom tabs existants — les tabs system sont reset
            const preservedCustomTabs = {};
            Object.keys(existingTabs).forEach(tabId => {
                if (existingTabs[tabId].type === 'custom') {
                    preservedCustomTabs[tabId] = existingTabs[tabId];
                }
            });
            console.log('🔒 Preserving custom tabs:', Object.keys(preservedCustomTabs));

            // Construire les widgets prédéfinis du dashboard depuis dashboardConfig
            const defaultDashboardWidgets = {};
            if (dashboardConfig && dashboardConfig.widgets) {
                dashboardConfig.widgets.forEach(widget => {
                    defaultDashboardWidgets[widget.id] = {
                        widgetId: widget.id,
                        type: widget.type,
                        queryId: widget.queryId,
                        title: widget.title,
                        titleKey: widget.titleKey,
                        config: widget.config || {},
                        isPredefined: true,
                        createdAt: new Date().toISOString()
                    };
                });
            }

            // Structure : dashboard/calls system + custom tabs préservés
            const defaultTabs = {
                dashboard: {
                    displayName: "Dashboard",
                    type: "system",
                    widgets: defaultDashboardWidgets,
                    widgetsPositions: { ...DEFAULT_WIDGET_POSITIONS },
                    createdAt: new Date().toISOString()
                },
                calls: {
                    displayName: "Calls List",
                    type: "system",
                    widgets: {},
                    widgetsPositions: {},
                    createdAt: new Date().toISOString()
                },
                ...preservedCustomTabs
            };

            const dashboardLayoutData = {
                tabs: defaultTabs
            };

            console.log('💾 Saving default layout to backend (custom tabs preserved)...', dashboardLayoutData);

            const response = await saveDashboardLayout(
                tenantId,
                analyticsProfile.id,
                dashboardLayoutData,
                token
            );

            if (response.success) {
                // Mettre à jour l'état local avec les positions par défaut
                setTabWidgetsPositions({
                    dashboard: { ...DEFAULT_WIDGET_POSITIONS },
                    calls: {}
                });
                setTabLayouts({
                    dashboard: { ...DEFAULT_WIDGET_POSITIONS },
                    calls: {}
                });
            } else {
                console.error('❌ Failed to save default layout:', response.message);
            }
        } catch (error) {
            console.error('❌ Error initializing default layout:', error);
        }
    }, [tenantId, token, currentScopeData]);

    /**
     * Sauvegarder le Dashboard Layout complet dans le backend
     */
    const saveDashboardLayoutToBackend = useCallback(async (explicitWidgetInstances = null) => {
        // ✅ Utiliser le state explicite si fourni, sinon utiliser widgetInstances du closure
        const currentWidgetInstances = explicitWidgetInstances || widgetInstances;
        
        if (!tenantId || !token || !decodedScope || !analyticsProfiles) {
            console.warn('⚠️ Cannot save dashboard layout: missing params');
            return;
        }

        // 🔹 Trouver le profile analytics par le champ 'scope'
        const analyticsProfile = Object.values(analyticsProfiles).find(
            profile => profile.scope === decodedScope
        );

        if (!analyticsProfile) {
            console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
            return;
        }

        try {
            // ✅ STEP 1: D'abord, forcer la mise à jour de tabWidgetsPositions avec layouts.lg actuel
            if (layouts && layouts.lg) {
                const currentPositions = {};
                layouts.lg.forEach(item => {
                    currentPositions[item.i] = {
                        x: item.x,
                        y: item.y,
                        w: item.w,
                        h: item.h
                    };
                });
                
                setTabWidgetsPositions(prev => ({
                    ...prev,
                    dashboard: currentPositions
                }));
            }
            
            let dashboardPositions = {};
            if (layouts && layouts.lg) {
                layouts.lg.forEach(item => {
                    dashboardPositions[item.i] = {
                        x: item.x,
                        y: item.y,
                        w: item.w,
                        h: item.h
                    };
                });
            } else if (tabWidgetsPositions['dashboard']) {
                dashboardPositions = tabWidgetsPositions['dashboard'];
            }
            
            // ✅ Construire les widgets dashboard en FUSIONNANT templates + configs personnalisées
            const dashboardWidgets = {};
            if (dashboardConfig && dashboardConfig.widgets) {
                dashboardConfig.widgets.forEach(widget => {
                    const instanceId = `dashboard-${widget.id}`;
                    const customConfig = currentWidgetInstances[instanceId];  // ← Utiliser currentWidgetInstances
                    
                    dashboardWidgets[widget.id] = {
                        widgetId: widget.id,
                        type: widget.type,
                        queryId: widget.queryId,
                        title: widget.title,
                        titleKey: widget.titleKey,
                        config: widget.config || {},
                        isPredefined: true,
                        createdAt: new Date().toISOString()
                    };
                    
                    // ✅ Si le widget a une config personnalisée, la sauvegarder
                    if (customConfig) {
                        dashboardWidgets[instanceId] = {
                            widgetId: widget.id,
                            name: customConfig.name,
                            config: customConfig.config,
                            createdAt: customConfig.createdAt,
                            updatedAt: customConfig.updatedAt
                        };
                    }
                });
            }

            // Construire la nouvelle structure avec tabs imbriqués
            const tabs = {
                dashboard: {
                    displayName: "Dashboard",
                    type: "system",
                    widgets: dashboardWidgets, // ← Contient les templates + configs personnalisées
                    widgetsPositions: dashboardPositions,
                    createdAt: new Date().toISOString()
                },
                calls: {
                    displayName: "Calls List",
                    type: "system",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions['calls'] || {},
                    createdAt: new Date().toISOString()
                }
            };

            // Ajouter les custom tabs
            customTabs.forEach(tab => {
                tabs[tab.id] = {
                    displayName: tab.name,
                    type: "custom",
                    widgets: {},
                    widgetsPositions: tabWidgetsPositions[tab.id] || {},
                    createdAt: tab.createdAt || new Date().toISOString()
                };
            });

            // Répartir les widgets dans leurs tabs respectifs
            Object.keys(currentWidgetInstances).forEach(instanceId => {  // ← Utiliser currentWidgetInstances
                const instance = currentWidgetInstances[instanceId];  // ← Utiliser currentWidgetInstances
                const tabId = instance.tabId;
                if (tabs[tabId]) {
                    tabs[tabId].widgets[instanceId] = {
                        widgetId: instance.widgetId,
                        name: instance.name,
                        config: instance.config,
                        createdAt: instance.createdAt,
                        updatedAt: instance.updatedAt
                    };
                    
                   
                }
            });

            const dashboardLayoutData = {
                tabs: tabs
            };

         

            const response = await saveDashboardLayout(
                tenantId,
                analyticsProfile.id,
                dashboardLayoutData,
                token
            );

            if (response.success) {
                const totalPositions = Object.keys(dashboardPositions).length;
              
                // ✅ IMPORTANT: Recharger le layout depuis Cosmos pour synchroniser l'état local
                try {
                    const reloadResponse = await getDashboardLayout(
                        tenantId,
                        analyticsProfile.id,
                        token
                    );

                    if (reloadResponse.success && reloadResponse.data && reloadResponse.data.tabs) {
                      
                        const tabs = reloadResponse.data.tabs;
                        
                        // Recharger les positions des widgets par tab
                        const positions = {};
                        Object.keys(tabs).forEach(tabId => {
                            if (tabs[tabId].widgetsPositions) {
                                positions[tabId] = tabs[tabId].widgetsPositions;
                            }
                        });

                        // Marquer qu'on charge depuis Cosmos pour permettre la mise à jour du layout
                        isLoadingFromCosmosRef.current = true;

                        setTabWidgetsPositions(positions);
                        setTabLayouts(positions);
                    }
                } catch (reloadError) {
                    console.error('⚠️ Error reloading layout after save:', reloadError);
                }
            } else {
                console.error('❌ Failed to save dashboard layout:', response.message);
            }
        } catch (error) {
            console.error('❌ Error saving dashboard layout:', error);
        }
    }, [tenantId, token, decodedScope, analyticsProfiles, customTabs, widgetInstances, tabWidgetsPositions, layouts]);

    /**
     * Manual save only - triggered by Save Layout button
     * Auto-save removed per user request - all saves now happen when clicking "Save Layout"
     */

    /**
     * Gérer les tabs personnalisés : créer, éditer, supprimer
     */
    const handleCreateTab = async (tabName) => {
        const newTab = {
            id: `tab-${Date.now()}`,
            name: tabName,
            type: 'custom',
            createdAt: new Date().toISOString()
        };

        const updatedTabs = [...customTabs, newTab];
        setCustomTabs(updatedTabs);
        setSelectedTab(newTab.id); // Sélectionner le nouveau tab dans le Pivot

        // Sauvegarder dans le backend (Cosmos DB)
        if (tenantId && token && decodedScope && analyticsProfiles) {
            // 🔹 Trouver le profile analytics par le champ 'scope'
            const analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === decodedScope
            );

            if (!analyticsProfile) {
                console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
                return;
            }

            try {
              
                // Construire la structure tabs avec le nouveau tab
                const tabs = {
                    dashboard: {
                        displayName: "Dashboard",
                        type: "system",
                        widgets: {},
                        widgetsPositions: tabWidgetsPositions['dashboard'] || {},
                        createdAt: new Date().toISOString()
                    },
                    calls: {
                        displayName: "Calls List",
                        type: "system",
                        widgets: {},
                        widgetsPositions: tabWidgetsPositions['calls'] || {},
                        createdAt: new Date().toISOString()
                    }
                };

                // Ajouter tous les custom tabs (y compris le nouveau)
                updatedTabs.forEach(tab => {
                    tabs[tab.id] = {
                        displayName: tab.name,
                        type: "custom",
                        widgets: {},
                        widgetsPositions: tabLayouts[tab.id] || {},
                        createdAt: tab.createdAt || new Date().toISOString()
                    };
                });

                // Répartir les widgets dans leurs tabs
                Object.keys(widgetInstances).forEach(instanceId => {
                    const instance = widgetInstances[instanceId];
                    const tabId = instance.tabId;
                    if (tabs[tabId]) {
                        tabs[tabId].widgets[instanceId] = {
                            widgetId: instance.widgetId,
                            name: instance.name,
                            config: instance.config,
                            createdAt: instance.createdAt,
                            updatedAt: instance.updatedAt
                        };
                    }
                });

                const dashboardLayoutData = { tabs };

                const response = await saveDashboardLayout(
                    tenantId,
                    analyticsProfile.id,
                    dashboardLayoutData,
                    token
                );

                if (response.success) {
                    // Tab saved successfully
                } else {
                    console.error('❌ Failed to save new tab to backend:', response.message);
                }
            } catch (error) {
                console.error('❌ Error saving new tab to backend:', error);
            }
        }
    };

    const handleEditTab = async (tabId, newName) => {
        const updatedTabs = customTabs.map(tab =>
            tab.id === tabId ? { ...tab, name: newName } : tab
        );
        setCustomTabs(updatedTabs);

        // Le useEffect auto-save se chargera de sauvegarder dans le backend
    };

    const handleDeleteTab = async (tabId) => {
        // Supprimer toutes les instances de widgets associées à ce tab
        const updatedInstances = { ...widgetInstances };
        Object.keys(updatedInstances).forEach(instanceId => {
            if (updatedInstances[instanceId].tabId === tabId) {
                delete updatedInstances[instanceId];
            }
        });
        setWidgetInstances(updatedInstances);

        // Supprimer le tab
        const updatedTabs = customTabs.filter(t => t.id !== tabId);
        setCustomTabs(updatedTabs);

        // Supprimer le layout du tab
        const updatedLayouts = { ...tabLayouts };
        delete updatedLayouts[tabId];
        setTabLayouts(updatedLayouts);

        // Si le tab actif était supprimé, basculer sur Dashboard
        if (selectedTab === tabId) {
            setSelectedTab('dashboard');
        }

        // Sauvegarder dans le backend (Cosmos DB)
        if (tenantId && token && decodedScope && analyticsProfiles) {
            // 🔹 Trouver le profile analytics par le champ 'scope'
            const analyticsProfile = Object.values(analyticsProfiles).find(
                profile => profile.scope === decodedScope
            );

            if (!analyticsProfile) {
                console.warn(`⚠️ Cannot find analytics profile for scope "${decodedScope}"`);
                return;
            }

            try {
            
                // Construire la structure tabs sans le tab supprimé
                const tabs = {
                    dashboard: {
                        displayName: "Dashboard",
                        type: "system",
                        widgets: {},
                        widgetsPositions: tabWidgetsPositions['dashboard'] || {},
                        createdAt: new Date().toISOString()
                    },
                    calls: {
                        displayName: "Calls List",
                        type: "system",
                        widgets: {},
                        widgetsPositions: tabWidgetsPositions['calls'] || {},
                        createdAt: new Date().toISOString()
                    }
                };

                // Ajouter uniquement les custom tabs restants (sans le tab supprimé)
                updatedTabs.forEach(tab => {
                    tabs[tab.id] = {
                        displayName: tab.name,
                        type: "custom",
                        widgets: {},
                        widgetsPositions: updatedLayouts[tab.id] || {},
                        createdAt: tab.createdAt || new Date().toISOString()
                    };
                });

                // Répartir les widgets restants dans leurs tabs
                Object.keys(updatedInstances).forEach(instanceId => {
                    const instance = updatedInstances[instanceId];
                    const tabIdOfInstance = instance.tabId;
                    if (tabs[tabIdOfInstance]) {
                        tabs[tabIdOfInstance].widgets[instanceId] = {
                            widgetId: instance.widgetId,
                            name: instance.name,
                            config: instance.config,
                            createdAt: instance.createdAt,
                            updatedAt: instance.updatedAt
                        };
                    }
                });

                const dashboardLayoutData = { tabs };

                const response = await saveDashboardLayout(
                    tenantId,
                    analyticsProfile.id,
                    dashboardLayoutData,
                    token
                );

                if (response.success) {
                    // Tab deletion saved
                } else {
                    console.error('❌ Failed to save tab deletion to backend:', response.message);
                }
            } catch (error) {
                console.error('❌ Error saving tab deletion to backend:', error);
            }
        }
    };

    /**
     * Obtenir les instances de widgets pour un tab personnalisé
     */
    const getWidgetInstancesForTab = (tabId) => {
        const instances = Object.values(widgetInstances).filter(instance => instance.tabId === tabId);

        // Convertir les instances en format widget pour le grid avec catalogWidget et originalConfig
        return instances.map(instance => {
            const catalogWidget = WIDGET_CATALOG.find(w => w.id === instance.widgetId);

            // ✅ Si c'est un widget du Dashboard par défaut, récupérer sa config originale
            let originalConfig = null;
            if (dashboardConfig && dashboardConfig.widgets) {
                originalConfig = dashboardConfig.widgets.find(w => w.id === instance.widgetId);
            }

            return {
                ...instance,
                catalogWidget,
                originalConfig  // Ajouter la config originale du dashboard si elle existe
            };
        });
    };

    /**
     * Ouvrir le dialog pour déplacer un widget
     */
    const handleMoveWidgetInstance = (instanceId) => {
        handleEditWidgetInstance(instanceId);
    };

    /**
     * Exporter un widget individuel vers Excel
     */
    const handleExportWidgetToExcel = async (widgetInstance) => {
        try {
            // Déterminer les filtres du widget
            let widgetFilters;
            if (widgetInstance.config?.useGlobalFilters !== false) {
                widgetFilters = appliedFilters;
            } else {
                const customPeriod = widgetInstance.config?.customPeriod;
                const customFilters = widgetInstance.config?.customFilters || {};
                
                widgetFilters = {
                    scope: decodedScope,
                    timezone: selectedTimezone
                };
                
                if (customPeriod) {
                    const dateRange = calculateDateRangeFromPeriod(customPeriod);
                    widgetFilters.startDate = dateRange.startDate;
                    widgetFilters.endDate = dateRange.endDate;
                }
                
                if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
                if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
                if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
            }

            // Récupérer les données du widget
            const widgetData = widgetsData[widgetInstance.widgetId];
            const widgetName = widgetInstance.name || widgetInstance.catalogWidget?.name || 'widget';
            const widgetType = widgetInstance.originalConfig?.type || widgetInstance.catalogWidget?.type;

            if (!widgetData) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
                return;
            }

            // Créer un workbook Excel
            const XLSX = await import('xlsx');
            const wb = XLSX.utils.book_new();

            // Helper function to format duration (seconds to mm:ss)
            const formatDuration = (seconds) => {
                if (seconds === null || seconds === undefined) return '';
                const mins = Math.floor(seconds / 60);
                const secs = Math.floor(seconds % 60);
                return `${mins}:${secs.toString().padStart(2, '0')}`;
            };

            // Préparer les données pour Excel selon le type de widget
            let wsData = [];
            
            // Check if it's a TABLE widget with rows and columns
            if (widgetType === 'table' && Array.isArray(widgetData) && widgetData.length > 0) {
                // For table widgets, export the entire table structure
                const firstRow = widgetData[0];
                
                // Detect label column (kpi, range, or first key)
                const labelKey = firstRow.hasOwnProperty('kpi') ? 'kpi' :
                    firstRow.hasOwnProperty('range') ? 'range' :
                        Object.keys(firstRow)[0];
                
                // Filter out metadata keys
                const metadataKeys = ['kpi', 'range', 'format', 'agentName', 'flowName', 'queueName', 'agentId', 'flow', 'queue'];
                const dataKeys = Object.keys(firstRow).filter(key => !metadataKeys.includes(key));
                
                // Build Excel rows with formatting
                wsData = widgetData.map(row => {
                    const excelRow = {};
                    // Add label column
                    excelRow[labelKey === 'kpi' ? 'KPI' : labelKey === 'range' ? 'Range' : labelKey] = row[labelKey] || '';
                    
                    // Add data columns with formatting
                    dataKeys.forEach(key => {
                        let value = row[key];
                        
                        // Apply format if row has format metadata and key is not a label
                        if (row.format === 'duration' && value !== undefined && value !== null) {
                            value = formatDuration(value);
                        } else if (value === undefined || value === null) {
                            value = '';
                        }
                        
                        excelRow[key] = value;
                    });
                    
                    return excelRow;
                });
            } else if (Array.isArray(widgetData)) {
                // Pour les graphiques (array de données)
                wsData = widgetData.map(item => ({
                    'Label': item.label || item.name || '',
                    'Value': item.value || item.count || 0,
                    'Percentage': item.percentage ? `${item.percentage}%` : ''
                }));
            } else if (typeof widgetData === 'object') {
                // Pour les métriques (objet simple)
                wsData = [{
                    'Metric': widgetName,
                    'Value': widgetData.value || 0,
                    'Trend': widgetData.trend || 0,
                    'Max Value': widgetData.maxValue || '',
                    'Target Value': widgetData.targetValue || ''
                }];
            }

            // Créer la feuille Excel
            const ws = XLSX.utils.json_to_sheet(wsData);
            
            // Ajouter la feuille au workbook
            XLSX.utils.book_append_sheet(wb, ws, widgetName.substring(0, 31));

            // Générer le fichier Excel
            const startDate = new Date(widgetFilters.startDate).toLocaleDateString('en-US').replace(/\//g, '-');
            const endDate = new Date(widgetFilters.endDate).toLocaleDateString('en-US').replace(/\//g, '-');
            const filename = `${widgetName}_${startDate}_to_${endDate}.xlsx`;
            
            XLSX.writeFile(wb, filename);
            
        } catch (error) {
            console.error('❌ Error exporting widget to Excel:', error);
            alert(t('EXPORT_ERROR') || 'Error exporting data');
        }
    };

    /**
     * Rendre le menu contextuel d'un widget (3 points)
     */
    const renderWidgetMenu = (widgetInstance) => {
        return (
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        icon={<FiMoreVertical size={20} />}
                        size="small"
                        style={{ minWidth: 'auto', padding: '4px 8px' }}
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem
                            icon={<Edit24Regular />}
                            onClick={() => handleEditWidgetInstance(widgetInstance.id)}
                        >
                            {t('EDIT_WIDGET') || 'Edit Widget'}
                        </MenuItem>
                        {/* <MenuItem
                            icon={<ArrowMove24Regular />}
                            onClick={() => handleMoveWidgetInstance(widgetInstance.id)}
                        >
                            MOVE_TO_TAB
                        </MenuItem> */}
                        <MenuItem
                            icon={<ArrowDownload24Regular />}
                            onClick={() => handleExportWidgetToExcel(widgetInstance)}
                        >
                            {t('EXPORT_EXCEL') || 'Export Excel'}
                        </MenuItem>
                        <MenuItem
                            icon={<ArrowDownload24Regular />}
                            onClick={() => {
                                // TODO: Implémenter l'export PDF
                            }}
                        >
                            {t('EXPORT_PDF') || 'Export PDF'}
                        </MenuItem>
                        {/* <MenuItem
                            icon={<FiRotateCcw size={20} />}
                            onClick={() => {
                                // TODO: Implémenter restore default
                            }}
                        >
                            RESTORE_DEFAULT
                        </MenuItem>
                        <MenuItem
                            icon={<Delete24Regular />}
                            onClick={() => handleDeleteWidgetInstance(widgetInstance.id)}
                        >
                            Delete
                        </MenuItem> */}
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    };

    /**
     * Rendre le contenu d'un widget instance avec les vraies données du dashboard
     */
    const renderWidgetContent = (widgetInstance) => {
        // widgetInstance contient : { id, widgetId, name, tabId, config, catalogWidget, originalConfig }
        const catalogWidget = widgetInstance.catalogWidget;
        const widgetConfig = widgetInstance.originalConfig; // Config originale du dashboard

        // Appliquer le titre personnalisé si défini
        const widgetTitle = widgetInstance.name || catalogWidget?.name || '';
        
        // 🔹 Déterminer les filtres à utiliser pour ce widget
        let widgetFilters;
        
        if (widgetInstance.config?.useGlobalFilters !== false) {
            widgetFilters = appliedFilters;
        } else {
            const customPeriod = widgetInstance.config?.customPeriod;
            const customFilters = widgetInstance.config?.customFilters || {};
            
            widgetFilters = {
                scope: decodedScope,
                timezone: selectedTimezone
            };
            
            if (customPeriod) {
                const dateRange = calculateDateRangeFromPeriod(customPeriod);
                widgetFilters.startDate = dateRange.startDate;
                widgetFilters.endDate = dateRange.endDate;
            }
            
            if (customFilters.flowKey) widgetFilters.flowKey = customFilters.flowKey;
            if (customFilters.queueKey) widgetFilters.queueKey = customFilters.queueKey;
            if (customFilters.agentKey) widgetFilters.agentKey = customFilters.agentKey;
            
          
        }
        
        const widgetData = widgetsData[widgetInstance.widgetId];
        
      
        // ✅ Si c'est un widget du Dashboard avec config originale, utiliser le composant Widget.js avec vraies données
        if (widgetConfig) {
            return (
                <div
                    className="widget-instance-content"
                >
                    {widgetInstance.config?.showTitle !== false && (
                        <div className="widget-instance-header">
                            <h3>{widgetTitle}</h3>
                            <div className="widget-instance-actions">
                                {renderWidgetMenu(widgetInstance)}
                            </div>
                        </div>
                    )}
                    {/* Utiliser le composant Widget existant avec les filtres appropriés */}
                    <Widget
                        config={widgetConfig}
                        filterValues={widgetFilters}
                        isEditMode={false}
                        tenantId={tenantId}
                        token={token}
                        preloadedData={widgetData}
                        isLoading={isLoadingWidgets}
                        onEdit={() => handleEditWidgetInstance(widgetInstance.id)}
                        onMove={() => handleMoveWidgetInstance(widgetInstance.id)}
                        onDelete={() => handleDeleteWidgetInstance(widgetInstance.id)}
                    />
                </div>
            );
        }

        // Pour les nouveaux widgets ajoutés depuis le catalogue, utiliser les widgets statiques
        const staticData = STATIC_WIDGET_DATA[widgetInstance.widgetId];
        
        if (staticData && catalogWidget) {
            switch (catalogWidget.type) {
                case 'metric':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentMetricWidget
                                    title={staticData.title}
                                    value={staticData.value}
                                    subtitle={staticData.subtitle}
                                    trend={staticData.trend}
                                    color={staticData.color || catalogWidget.color}
                                />
                            </div>
                        </div>
                    );

                case 'bar':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'donut':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentDonutChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'line':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentLineChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'gauge':
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentHorizontalBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                // Types additionnels - utiliser les mêmes composants selon le type de données
                case 'sparkline':
                case 'area':
                    // Utiliser le LineChart pour sparkline et area
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentLineChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'pie':
                    // Utiliser DonutChart pour pie
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentDonutChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'grouped-bar':
                case 'heatmap':
                case 'funnel':
                case 'scatter':
                    // Utiliser BarChart pour ces types
                    return (
                        <div className="widget-instance-content">
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                case 'progress':
                    // Utiliser HorizontalBarChart pour progress
                    return (
                        <div
                            className="widget-instance-content"
                        >
                            {widgetInstance.config?.showTitle !== false && (
                                <div className="widget-instance-header">
                                    <h3>{widgetTitle}</h3>
                                    <div className="widget-instance-actions">
                                        {renderWidgetMenu(widgetInstance)}
                                    </div>
                                </div>
                            )}
                            <div className="widget-instance-body">
                                <FluentHorizontalBarChartWidget
                                    title={staticData.title}
                                    data={staticData.data}
                                    color={staticData.color || catalogWidget.color}
                                    height={250}
                                />
                            </div>
                        </div>
                    );

                default:
                    break;
            }
        }

        // Fallback : widget non supporté ou sans données
        return (
            <div
                className="widget-instance-content"
            >
                {widgetInstance.config?.showTitle !== false && (
                    <div className="widget-instance-header">
                        <h3>{widgetTitle}</h3>
                        <div className="widget-instance-actions">
                            {renderWidgetMenu(widgetInstance)}
                        </div>
                    </div>
                )}
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
                    <div>Widget "{widgetTitle}"</div>
                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        Type: {catalogWidget?.type || 'unknown'}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Restaurer un widget à sa position et taille par défaut
     */
    const handleRestoreWidget = (widgetId) => {
        const defaultPosition = DEFAULT_WIDGET_POSITIONS[widgetId];

        if (!defaultPosition) {
            console.warn(`No default position found for widget: ${widgetId}`);
            return;
        }

        // Mettre à jour le layout pour lg breakpoint
        setLayouts(prevLayouts => {
            const newLayouts = { ...prevLayouts };

            // Mettre à jour pour tous les breakpoints
            Object.keys(newLayouts).forEach(breakpoint => {
                const layoutForBreakpoint = newLayouts[breakpoint];
                const widgetLayoutIndex = layoutForBreakpoint.findIndex(item => item.i === widgetId);

                if (widgetLayoutIndex !== -1) {
                    // Restaurer la position par défaut
                    layoutForBreakpoint[widgetLayoutIndex] = {
                        ...layoutForBreakpoint[widgetLayoutIndex],
                        x: defaultPosition.x,
                        y: defaultPosition.y,
                        w: defaultPosition.w,
                        h: defaultPosition.h
                    };
                }
            });

            return newLayouts;
        });
    };

    /**
     * Éditer les filtres d'un widget du Dashboard
     * Crée ou charge une instance virtuelle pour stocker les filtres personnalisés
     */
    const handleEditDashboardWidget = (widgetId) => {
        // Trouver le widget dans la config du dashboard
        const widgetConfig = dashboardConfig?.widgets?.find(w => w.id === widgetId);
        if (!widgetConfig) {
            return;
        }

        // Trouver le widget dans le catalogue (ou créer une entrée temporaire)
        let catalogWidget = WIDGET_CATALOG.find(w => w.id === widgetId);
        if (!catalogWidget) {
            // Créer une entrée catalogue temporaire basée sur la config du dashboard
            catalogWidget = {
                id: widgetId,
                name: widgetConfig.title,
                description: widgetConfig.description,
                category: 'dashboard',
                type: widgetConfig.type,
                defaultSize: { w: widgetConfig.layout.w, h: widgetConfig.layout.h }
            };
        }

        // Chercher si une instance existe déjà pour ce widget du dashboard
        // Les widgets du dashboard ont un instanceId spécial: "dashboard-{widgetId}"
        const instanceId = `dashboard-${widgetId}`;
        let existingInstance = widgetInstances[instanceId];
        
        if (!existingInstance) {
            // Créer une instance virtuelle pour stocker la config de filtres
            existingInstance = {
                id: instanceId,
                widgetId: widgetId,
                name: widgetConfig.title,
                tabId: 'dashboard',
                enabled: true,
                config: {
                    useGlobalFilters: true,  // Par défaut, utilise les filtres globaux
                    customPeriod: 'month',
                    customFilters: {}
                }
            };
        }

        // Ouvrir le panel à droite pour éditer le widget
        setSelectedWidget(catalogWidget);
        setEditingInstance(existingInstance);  // Passer l'instance (nouvelle ou existante)
        setShowConfigPanel(true);
    };

    /**
     * Déplacer/copier un widget du Dashboard vers un autre tab
     */
    const handleMoveDashboardWidget = (widgetId) => {
        // Identique à Edit mais avec focus sur le tab de destination
        handleEditDashboardWidget(widgetId);
    };

    /**
     * Masquer un widget du Dashboard
     */
    const handleDeleteDashboardWidget = (widgetId) => {
        if (window.confirm('Voulez-vous masquer ce widget du Dashboard ?')) {
            // TODO: Implémenter la logique de masquage
            // Pour l'instant, on pourrait stocker une liste de widgets masqués
            // Vous pourriez ajouter un state pour les widgets masqués:
            // setHiddenWidgets(prev => [...prev, widgetId]);
        }
    };

    /**
     * Gérer le changement de layout du dashboard
     * Sauvegarde automatiquement dans Cosmos quand on déplace un widget
     */
    const handleLayoutChange = (layout, layouts) => {
        setLayouts(layouts);

        // Marquer la fin du drag/resize
        setIsDragging(false);
        setIsResizing(false);

        // Si on est en mode édition, mettre à jour tabWidgetsPositions pour le dashboard
        if (isEditMode && layouts && layouts.lg) {
            const updatedPositions = {};
            layouts.lg.forEach(item => {
                updatedPositions[item.i] = {
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h
                };
            });
            
            // Mettre à jour l'état local
            setTabWidgetsPositions(prev => ({
                ...prev,
                dashboard: updatedPositions
            }));
        }
    };

    /**
     * Gérer le changement de layout pour les widgets dans les tabs customisés
     * Met à jour l'état local - sera sauvegardé dans Cosmos quand on clique "Save Layout"
     */
    const handleTabWidgetLayoutChange = (tabId, layout) => {
        // Convertir le layout en format {instanceId: {x, y, w, h}}
        const positions = {};
        layout.forEach(item => {
            positions[item.i] = {
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h
            };
        });

        // Mettre à jour les deux états (tabLayouts et tabWidgetsPositions pour backend)
        const updatedLayouts = {
            ...tabLayouts,
            [tabId]: positions
        };
        setTabLayouts(updatedLayouts);

        const updatedWidgetsPositions = {
            ...tabWidgetsPositions,
            [tabId]: positions
        };
        setTabWidgetsPositions(updatedWidgetsPositions);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%', position: 'relative', overflow: 'hidden' }}>
            {/* Left Side: Widget Catalog Panel */}
            {showCatalogPanel && (
                <AnalyticsWidgetCatalogPanel
                    isOpen={showCatalogPanel}
                    onToggle={setShowCatalogPanel}
                    onWidgetDragStart={handleWidgetDragStart}
                    onWidgetClick={handleWidgetClick}
                    availableWidgets={dashboardConfig?.widgets?.map(w => w.id) || []}
                    initiallyCollapsed={catalogInitiallyCollapsed}
                />
            )}

            {/* Main Content Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '20px',
                marginLeft: '0',
                transition: 'margin-left 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Drop zone overlay */}
                {dropZoneActive && draggedWidget && (
                    <div className="analytics-drop-zone active">
                        <div className="analytics-drop-zone-text">
                            <FiGrid size={24} />
                            Drop widget here to configure
                        </div>
                    </div>
                )}

                {/* En-tête du dashboard - FIXED */}
                <div className="analytics-header-fixed">
                    {/* Gauche: Bouton retour */}
                    <div className="analytics-header-left">
                        <IconButton
                            title={t('BACK_TO_SCOPES')}
                            ariaLabel={t('BACK_TO_SCOPES')}
                            onClick={() => navigate('/analytics')}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                }
                            }}
                        >
                            <FiArrowLeft style={{ fontSize: 16, color: 'white' }} />
                        </IconButton>
                    </div>

                    {/* Centre: Date range + Nom du scope */}
                    <div className="analytics-header-center">
                        <p style={{ margin: 0, color: '#323130', fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {decodedScope}
                            {appliedFilters && appliedFilters.startDate && appliedFilters.endDate && (
                                <span
                                    onClick={() => setShowFilters(!showFilters)}
                                    style={{ 
                                        fontSize: '12px', 
                                        fontWeight: 400, 
                                        color: 'rgb(96, 94, 92)', 
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#5b5fc7'}
                                    onMouseLeave={(e) => e.target.style.color = 'rgb(96, 94, 92)'}
                                >
                                    ({new Date(appliedFilters.startDate).toLocaleDateString()} - {new Date(appliedFilters.endDate).toLocaleDateString()})
                                </span>
                            )}
                            {isLoadingWidgets && <Spinner size={SpinnerSize.small} />}
                        </p>
                    </div>

                    {/* Droite: Boutons Edit, Filter et PDF */}
                    <div className="analytics-header-right">
                        <IconButton
                            title={isEditMode ? t('SAVE_LAYOUT') : t('EDIT_LAYOUT')}
                            ariaLabel={isEditMode ? t('SAVE_LAYOUT') : t('EDIT_LAYOUT')}
                            onClick={async () => {
                                if (isEditMode) {
                                    // Save all tabs, widgets, and positions to backend
                                    await saveDashboardLayoutToBackend();
                                    // Exit edit mode and close catalog
                                    setIsEditMode(false);
                                    setShowCatalogPanel(false);
                                } else {
                                    // Enter edit mode and open catalog collapsed
                                    setIsEditMode(true);
                                    setShowCatalogPanel(true);
                                    setCatalogInitiallyCollapsed(true);
                                }
                            }}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: isEditMode ? '#107c10' : '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: isEditMode ? '#0e6b0e' : '#4a4fb0'
                                }
                            }}
                        >
                            {isEditMode ? (
                                <FiSave style={{ fontSize: 16, color: 'white' }} />
                            ) : (
                                <FiEdit2 style={{ fontSize: 16, color: 'white' }} />
                            )}
                        </IconButton>
                        <IconButton
                            title={t('TOGGLE_FILTERS')}
                            ariaLabel={t('TOGGLE_FILTERS')}
                            onClick={() => setShowFilters(!showFilters)}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: showFilters ? '#4a4fb0' : '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                }
                            }}
                        >
                            <FiFilter style={{ fontSize: 16, color: 'white' }} />
                        </IconButton>
                        {/* Catalog button removed - now controlled by Edit button only */}
                        {/* Manage Tabs button removed - now handled by + icon in tabs */}
                        <IconButton
                            title={t('EXPORT_PDF')}
                            ariaLabel={t('EXPORT_PDF')}
                            onClick={handleExportPDF}
                            disabled={!appliedFilters || isExporting}
                            styles={{
                                root: {
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#5b5fc7',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4a4fb0'
                                },
                                rootDisabled: {
                                    backgroundColor: '#e1dfdd',
                                    opacity: 0.5
                                }
                            }}
                        >
                            {isExporting ? (
                                <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                                <FiDownload style={{ fontSize: 16, color: 'white' }} />
                            )}
                        </IconButton>
                    </div>
                </div>

                {/* Filtres - affichés conditionnellement */}
                {showFilters && (
                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                            {/* Sélecteur de timezone - icône seulement */}
                            <Dropdown
                                selectedKey={selectedTimezone}
                                onChange={(e, option) => setSelectedTimezone(option.key)}
                                options={COMMON_TIMEZONES}
                                onRenderTitle={(options) => <span style={{ fontSize: '16px' }}>🕐</span>}
                                onRenderOption={(option) => <span>{option.text}</span>}
                                styles={{
                                    root: { width: 45, minWidth: 45 },
                                    dropdown: {
                                        fontSize: 14,
                                        paddingLeft: '8px',
                                        paddingRight: '8px',
                                        height: '32px',
                                        minHeight: '32px'
                                    },
                                    title: {
                                        padding: '0 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '32px',
                                        lineHeight: '32px'
                                    },
                                    dropdownOptionText: {
                                        overflow: 'visible',
                                        whiteSpace: 'normal'
                                    },
                                    callout: {
                                        minWidth: '250px'
                                    }
                                }}
                                title={t('TIMEZONE')}
                            />

                            {/* Filtres de date */}
                            <DatePicker
                                value={filterValues.startDate}
                                onSelectDate={handleStartDateChange}
                                placeholder={t('SELECT_DATE')}
                                ariaLabel={t('START_DATE')}
                                formatDate={(date) => date.toLocaleDateString('fr-FR')}
                                maxDate={new Date()}
                                styles={{
                                    root: { width: 130 },
                                    textField: { height: 32 }
                                }}
                            />
                            <DatePicker
                                value={filterValues.endDate}
                                onSelectDate={handleEndDateChange}
                                placeholder={t('SELECT_DATE')}
                                ariaLabel={t('END_DATE')}
                                formatDate={(date) => date.toLocaleDateString('fr-FR')}
                                maxDate={new Date()}
                                styles={{
                                    root: { width: 130 },
                                    textField: { height: 32 }
                                }}
                            />

                            {/* Dynamic Filters (Flow, Queue, Agent) - inline */}
                            <DashboardFilters
                                tenantId={tenantId}
                                scope={decodedScope}
                                onFilterChange={handleDynamicFilterChange}
                                initialFilters={dynamicFilters}
                            />

                            {/* Bouton Apply tout à droite */}
                            <Button
                                appearance="primary"
                                onClick={handleApplyFilters}
                                disabled={!filterValues.startDate || !filterValues.endDate}
                                style={{ height: '32px', marginLeft: 'auto' }}
                            >
                                {t('APPLY')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Pivot principal - Dashboard, Calls List ET tabs personnalisés au même niveau */}
                {appliedFilters && (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                        <Pivot
                            aria-label="Dashboard Navigation"
                            selectedKey={selectedTab}
                            onLinkClick={(item) => {
                                setSelectedTab(item.props.itemKey);
                            }}
                            headersOnly={true}
                            styles={{
                                root: { display: 'inline-flex', gap: '0' },
                                link: {
                                    marginLeft: '0px',
                                    marginRight: '0px',
                                    padding: '0 8px',
                                    '::after': {
                                        left: '0px',
                                        right: '0px'
                                    }
                                },
                                linkIsSelected: {
                                    marginLeft: '0px',
                                    marginRight: '0px',
                                    padding: '0 8px',
                                    '::before': {
                                        left: '0px',
                                        right: '0px'
                                    }
                                }
                            }}
                        >
                            <PivotItem headerText={t('DASHBOARD')} itemKey="dashboard" />
                            {customTabs.map((tab) => {
                                const localizedName = t(`TAB_${String(tab.id).toUpperCase()}`, { defaultValue: tab.name });
                                return (
                                    <PivotItem
                                        key={tab.id}
                                        headerText={
                                            isEditMode ? (
                                                <div >
                                                    <span>{localizedName}</span>
                                                    <Button
                                                        appearance="subtle"
                                                        icon={<DismissFilled />}
                                                        aria-label="Delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteTabConfirm({
                                                                open: true,
                                                                tabId: tab.id,
                                                                tabName: localizedName
                                                            });
                                                        }}
                                                        className="tab-delete-button"
                                                    />
                                                </div>
                                            ) : localizedName
                                        }
                                        itemKey={tab.id}
                                    />
                                );
                            })}
                            <PivotItem headerText={t('CALLS_LIST')} itemKey="calls" />
                        </Pivot>
                        <IconButton
                            title={t('ADD_TAB') || 'Add Tab'}
                            ariaLabel="Add Tab"
                            onClick={() => {
                                setNewTabName('');
                                setIsAddTabDialogOpen(true);
                            }}
                            styles={{
                                root: {
                                    marginTop: "5px",
                                    width: 30,
                                    height: 30,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    marginLeft: '8px',
                                    paddingBottom: '2px'
                                },
                                rootHovered: {
                                    backgroundColor: 'transparent'
                                }
                            }}
                        >
                            <span style={{ fontSize: 28, color: '#605e5c', fontWeight: 'bold' }}>+</span>
                        </IconButton>
                    </div>
                )}

                {/* Scrollable content */}
                <div
                    className="analytics-content-scrollable"
                    onDrop={handleDashboardDrop}
                    onDragOver={handleDashboardDragOver}
                    onDragLeave={handleDashboardDragLeave}
                >
                    {/* Dashboard Tab - Ancien système de widgets */}
                    {selectedTab === 'dashboard' && (
                        <div id="dashboard-container">
                            {/* Edit mode info message */}
                            {isEditMode && (
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                    onDismiss={() => setIsEditMode(false)}
                                    dismissButtonAriaLabel={t('SAVE_LAYOUT')}
                                    style={{ marginBottom: '15px' }}
                                >
                                    {t('EDIT_MODE_INFO')}
                                </MessageBar>
                            )}

                            {/* Discrete progress bar during loading */}
                            {isLoadingWidgets && (
                                <div style={{
                                    width: '100%',
                                    height: '2px',
                                    backgroundColor: '#f3f2f1',
                                    marginBottom: '10px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        width: '40%',
                                        backgroundColor: '#0078d4',
                                        animation: 'progressBar 1.5s ease-in-out infinite'
                                    }} />
                                </div>
                            )}

                            {/* Widget Grid - Génération dynamique du layout depuis tabWidgetsPositions */}
                            {dashboardConfig && dashboardConfig.widgets && appliedFilters && (

                                <ResponsiveGridLayout
                                    key="dashboard-main"
                                    className="layout"
                                    layouts={{
                                        lg: dashboardConfig.widgets
                                            .filter(widget => DASHBOARD_WIDGET_IDS.includes(widget.id))
                                            .map(widget => {
                                            // Utiliser les positions sauvegardées depuis Cosmos
                                            const savedPos = tabWidgetsPositions['dashboard']?.[widget.id];
                                            // Fallback sur DEFAULT_WIDGET_POSITIONS si pas de position sauvegardée
                                            const defaultPos = DEFAULT_WIDGET_POSITIONS[widget.id];
                                            const pos = savedPos || defaultPos || { x: 0, y: 0, w: 3, h: 2 };

                                            return {
                                                i: widget.id,
                                                x: pos.x,
                                                y: pos.y,
                                                w: pos.w,
                                                h: pos.h,
                                                minW: widget.layout?.minW || 2,
                                                minH: widget.layout?.minH || 2,
                                                static: false
                                            };
                                        })
                                    }}
                                    breakpoints={{ lg: 996, md: 768, sm: 480, xs: 0 }}
                                    cols={{ lg: 12, md: 12, sm: 12, xs: 12 }}
                                    rowHeight={100}
                                    onLayoutChange={handleLayoutChange}
                                    onDragStart={() => setIsDragging(true)}
                                    onDragStop={() => setIsDragging(false)}
                                    onResizeStart={() => setIsResizing(true)}
                                    onResizeStop={() => setIsResizing(false)}
                                    isDraggable={isEditMode}
                                    isResizable={isEditMode}
                                    compactType={null}
                                    preventCollision={true}
                                    useCSSTransforms={true}
                                >
                                    {dashboardConfig.widgets
                                        .filter(widgetConfig => DASHBOARD_WIDGET_IDS.includes(widgetConfig.id))
                                        .map((widgetConfig) => (
                                        <div key={widgetConfig.id}>
                                            <Widget
                                                config={widgetConfig}
                                                filterValues={appliedFilters}
                                                isEditMode={isEditMode}
                                                tenantId={tenantId}
                                                token={token}
                                                preloadedData={widgetsData[widgetConfig.id]}
                                                isLoading={isLoadingWidgets}
                                                onRestore={handleRestoreWidget}
                                                onEdit={handleEditDashboardWidget}
                                                onMove={handleMoveDashboardWidget}
                                                onDelete={handleDeleteDashboardWidget}
                                            />
                                        </div>
                                    ))}
                                </ResponsiveGridLayout>
                            )}
                        </div>
                    )}

                    {/* Tabs personnalisés - Affichage direct quand sélectionné dans le Pivot */}
                    {selectedTab !== 'dashboard' && selectedTab !== 'calls' && customTabs.find(t => t.id === selectedTab) && (
                        <div id="dashboard-container">
                            {/* Edit mode info message */}
                            {isEditMode && (
                                <MessageBar
                                    messageBarType={MessageBarType.info}
                                    isMultiline={false}
                                    onDismiss={() => setIsEditMode(false)}
                                    dismissButtonAriaLabel={t('SAVE_LAYOUT')}
                                    style={{ marginBottom: '15px' }}
                                >
                                    {t('EDIT_MODE_INFO')}
                                </MessageBar>
                            )}

                            {/* Empty state - No widgets */}
                            {getWidgetInstancesForTab(selectedTab).length === 0 && (
                                <div className="dashboard-empty-state">
                                    <div className="empty-state-icon">📊</div>
                                    <div className="empty-state-title">No widgets in this tab</div>
                                    <div className="empty-state-description">
                                       Add widgets to this tab
                                    </div>
                                </div>
                            )}

                            {/* Widget Grid pour custom tabs avec drag & drop */}
                            {getWidgetInstancesForTab(selectedTab).length > 0 && (
                                <ResponsiveGridLayout
                                    className="layout"
                                    layouts={{
                                        lg: getWidgetInstancesForTab(selectedTab).map(inst => {
                                            const pos = tabLayouts[selectedTab]?.[inst.id];
                                            return {
                                                i: inst.id,
                                                x: pos?.x ?? 0,
                                                y: pos?.y ?? 0,
                                                w: pos?.w ?? 6,
                                                h: pos?.h ?? 4,
                                                minW: 2,
                                                minH: 2,
                                                maxW: 12,
                                                static: false
                                            };
                                        }),
                                        md: getWidgetInstancesForTab(selectedTab).map(inst => {
                                            const pos = tabLayouts[selectedTab]?.[inst.id];
                                            return {
                                                i: inst.id,
                                                x: pos?.x ?? 0,
                                                y: pos?.y ?? 0,
                                                w: Math.min(pos?.w ?? 5, 10),
                                                h: pos?.h ?? 4,
                                                minW: 2,
                                                minH: 2,
                                                maxW: 10,
                                                static: false
                                            };
                                        }),
                                        sm: getWidgetInstancesForTab(selectedTab).map(inst => {
                                            const pos = tabLayouts[selectedTab]?.[inst.id];
                                            return {
                                                i: inst.id,
                                                x: 0,
                                                y: pos?.y ?? 0,
                                                w: 6,
                                                h: pos?.h ?? 4,
                                                minW: 2,
                                                minH: 2,
                                                maxW: 6,
                                                static: false
                                            };
                                        })
                                    }}
                                    breakpoints={{ lg: 996, md: 768, sm: 480, xs: 0 }}
                                    cols={{ lg: 12, md: 12, sm: 12, xs: 12 }}
                                    rowHeight={100}
                                    onLayoutChange={(layout, layouts) => handleTabWidgetLayoutChange(selectedTab, layout)}
                                    onDragStart={() => setIsDragging(true)}
                                    onDragStop={() => setIsDragging(false)}
                                    onResizeStart={() => setIsResizing(true)}
                                    onResizeStop={() => setIsResizing(false)}
                                    isDraggable={isEditMode}
                                    isResizable={isEditMode}
                                    compactType={null}
                                    preventCollision={true}
                                    useCSSTransforms={true}
                                    resizeHandles={['se']}
                                    draggableCancel="button, .widget-instance-actions"
                                    autoSize={true}
                                >
                                    {getWidgetInstancesForTab(selectedTab).map(widgetInstance => (
                                        <div key={widgetInstance.id}>
                                            {renderWidgetContent(widgetInstance)}
                                        </div>
                                    ))}
                                </ResponsiveGridLayout>
                            )}
                        </div>
                    )}

                    {/* Calls List Tab */}
                    {selectedTab === 'calls' && appliedFilters && (
                        <div style={{ padding: '20px', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
                            {/* Action bar with export button */}
                            <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginBottom: '20px', flexShrink: 0 }}>
                                <Button
                                    appearance="primary"
                                    onClick={handleExportToExcel}
                                    disabled={isLoadingCalls || isExportingExcel || filteredCallsList.length === 0}
                                >
                                    📊 {t('EXPORT_TO_EXCEL')}
                                </Button>
                                {isLoadingCalls && (
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#0078D4',
                                        fontStyle: 'italic',
                                        lineHeight: '32px'
                                    }}>
                                      {t('LOADING')} 
                                    </span>
                                )}
                                {!isLoadingCalls && filteredCallsList.length > 0 && (
                                    <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
                                        {t('CALLS_DISPLAYED_INFO', { count: filteredCallsList.length })}
                                    </MessageBar>
                                )}
                            </Stack>

                            {!isLoadingCalls && callsList.length > 0 ? (
                                <div ref={tableContainerRef} style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', flex: 1 }}>
                                    <DetailsList
                                        items={filteredCallsList}
                                        columns={callsListColumns}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        selectionMode={SelectionMode.none}
                                        isHeaderVisible={true}
                                        getKey={(item) => item.uniqueKey || item.callId}
                                    />
                                </div>
                            ) : !isLoadingCalls && (
                                <MessageBar messageBarType={MessageBarType.info}>
                                    {t('NO_DATA_AVAILABLE')}
                                </MessageBar>
                            )}
                        </div>
                    )}
                </div>

                {/* Export progress dialog */}
                <Dialog
                    hidden={!showExportDialog}
                    onDismiss={closeExportDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('EXPORTING_EXCEL'),
                        subText: exportMessage
                    }}
                    modalProps={{
                        isBlocking: isExportingExcel,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <ProgressIndicator
                        label={exportProgress > 0 ? `${exportProgress}%` : ''}
                        percentComplete={exportProgress / 100}
                    />
                    {exportDownloadUrl && (
                        <Stack tokens={{ childrenGap: 15 }} style={{ marginTop: 20 }}>
                            <MessageBar messageBarType={MessageBarType.success}>
                                {t('EXPORT_COMPLETED')}
                            </MessageBar>
                        </Stack>
                    )}
                    <DialogFooter>
                        {exportDownloadUrl ? (
                            <>
                                <PrimaryButton
                                    onClick={async () => {
                                        try {
                                            const r = await fetch(exportDownloadUrl);
                                            const blob = await r.blob();
                                            const localUrl = URL.createObjectURL(blob);
                                            const dateStr = new Date().toISOString().slice(0, 10);
                                            const link = document.createElement('a');
                                            link.href = localUrl;
                                            link.download = `analytics-export_${decodedScope}_${dateStr}.xlsx`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            setTimeout(() => URL.revokeObjectURL(localUrl), 1000);
                                        } catch (e) {
                                            window.open(exportDownloadUrl, '_blank');
                                        }
                                    }}
                                    text={t('DOWNLOAD_EXCEL')}
                                />
                                <DefaultButton onClick={closeExportDialog} text={t('CLOSE')} />
                            </>
                        ) : (
                            <DefaultButton
                                onClick={closeExportDialog}
                                text={t('CLOSE')}
                                disabled={isExportingExcel}
                            />
                        )}
                    </DialogFooter>
                </Dialog>

                {/* Widget Catalog Dialog */}
                <WidgetCatalog
                    isOpen={isCatalogOpen}
                    onDismiss={() => setIsCatalogOpen(false)}
                    onSelectWidget={handleSelectWidgetFromCatalog}
                />

                {/* Widget Instance Configuration Dialog */}
                <WidgetInstanceConfig
                    isOpen={isInstanceConfigOpen}
                    onDismiss={() => setIsInstanceConfigOpen(false)}
                    widgetInfo={selectedWidgetInfo}
                    instance={editingInstance}
                    onSave={handleSaveWidgetInstance}
                    availableTabs={[
                        { id: 'dashboard', name: 'Dashboard', type: 'dashboard' },
                        { id: 'calls', name: 'Calls List', type: 'calls' },
                        ...customTabs
                    ]}
                    currentTabId={selectedTab}
                />

                {/* Tab Manager Dialog */}
                <TabManager
                    isOpen={isTabManagerOpen}
                    onDismiss={() => setIsTabManagerOpen(false)}
                    tabs={[
                        { id: 'dashboard', name: 'Dashboard', isSystemTab: true, type: 'dashboard' },
                        { id: 'calls', name: 'Calls List', isSystemTab: true, type: 'calls' },
                        ...customTabs
                    ]}
                    onCreateTab={handleCreateTab}
                    onEditTab={handleEditTab}
                    onDeleteTab={handleDeleteTab}
                    currentTabId={selectedTab}
                    onSelectTab={setSelectedTab}
                />

                {/* Confirm Delete Tab Dialog */}
                <ConfirmAlert
                    open={deleteTabConfirm.open}
                    onConfirm={() => {
                        handleDeleteTab(deleteTabConfirm.tabId);
                        setDeleteTabConfirm({ open: false, tabId: null, tabName: '' });
                    }}
                    onCancel={() => setDeleteTabConfirm({ open: false, tabId: null, tabName: '' })}
                    name={deleteTabConfirm.tabName}
                />

                {/* Add Tab Dialog - Same style as AddRoutingDialog */}
                <Dialog
                    open={isAddTabDialogOpen}
                    onOpenChange={(_, data) => {
                        if (!data.open) {
                            setIsAddTabDialogOpen(false);
                            setNewTabName('');
                        }
                    }}
                >
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>{t('ADD_NEW_TAB') || 'Add New Tab'}</DialogTitle>
                            <DialogContent className={dialogStyles.dialogContent}>
                                <div className={dialogStyles.inputContainer}>
                                    <Label required>{t('TAB_NAME') || 'Tab Name'}</Label>
                                    <Input
                                        value={newTabName}
                                        onChange={(_, data) => setNewTabName(data.value)}
                                        placeholder={t('ENTER_TAB_NAME_PLACEHOLDER') || 'e.g., Sales Dashboard'}
                                        autoFocus
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && newTabName.trim()) {
                                                handleCreateTab(newTabName.trim());
                                                setIsAddTabDialogOpen(false);
                                                setNewTabName('');
                                            }
                                        }}
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button
                                        appearance="secondary"
                                        onClick={() => {
                                            setIsAddTabDialogOpen(false);
                                            setNewTabName('');
                                        }}
                                    >
                                        {t('CANCEL') || 'Cancel'}
                                    </Button>
                                </DialogTrigger>
                                <Button
                                    appearance="primary"
                                    onClick={() => {
                                        if (newTabName.trim()) {
                                            handleCreateTab(newTabName.trim());
                                            setIsAddTabDialogOpen(false);
                                            setNewTabName('');
                                        }
                                    }}
                                    disabled={!newTabName.trim()}
                                >
                                    {t('ADD') || 'Add'}
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </div>

            {/* Right Side: Widget Configuration Panel */}
            <AnalyticsWidgetConfigPanel
                isOpen={showConfigPanel}
                widget={selectedWidget}
                widgetInstance={editingInstance}
                tabs={[
                    { id: 'dashboard', name: 'Dashboard' },
                    { id: 'calls', name: 'Calls List' },
                    ...customTabs
                ]}
                currentTabId={selectedTab}
                tenantId={tenantId}
                scope={decodedScope}
                onSave={handleConfigSave}
                onClose={handleConfigClose}
                onDelete={handleConfigDelete}
            />

            {/* Transcription Drawer */}
            <Drawer
                type="overlay"
                position="end"
                open={showTranscriptionPanel}
                onOpenChange={(_, { open }) => setShowTranscriptionPanel(open)}
                size="small"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<Dismiss24Regular />}
                                onClick={() => setShowTranscriptionPanel(false)}
                                aria-label={t('CLOSE')}
                            />
                        }
                    >
                        {t('TRANSCRIPTION')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {selectedTranscription && (
                        <div style={{ padding: '20px' }}>
                            {selectedTranscription.text ? (
                                <div style={{ 
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.6',
                                    fontSize: '14px',
                                    color: '#242424'
                                }}>
                                    {selectedTranscription.text}
                                </div>
                            ) : (
                                <MessageBar messageBarType={MessageBarType.info}>
                                    {t('NO_TRANSCRIPTION_AVAILABLE')}
                                </MessageBar>
                            )}
                        </div>
                    )}
                </DrawerBody>
            </Drawer>
        </div>
    );
};

export default AnalyticsDashboard;
